import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Skeleton,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Visibility as ViewIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIndicatorIcon,
  FileDownload as FileDownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
  CleaningServices as CleaningServicesIcon
} from '@mui/icons-material';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import {
  SchoolEnrichmentReviewDialog,
  SchoolTriageDialog,
} from './SchoolEnrichmentDialogs';
import api from '../../api';
import BookViewerDialog from '../../components/BookViewerDialog';
import { adminHasPermission, adminHasAnyPermission } from '../../utils/adminPermissions';
import { masterFormPermission } from '../../constants/masterFormPermissions';
import { sortHierarchyItems } from '../../utils/hierarchyOrdering';


// Exam stages edit as "Name | Subject | Questions | Minutes | To minutes". Keep every column's
// position (empty when unknown) — the save step reads by position, so dropping an empty
// question count would turn the minutes into the question count.
const stageToEditLine = (stage) => {
  const parts = [stage.name, stage.subject, stage.question_count, stage.duration_minutes, stage.duration_to_minutes].map((part) => (part == null ? '' : String(part)));
  while (parts.length && parts[parts.length - 1] === '') parts.pop();
  return parts.join(' | ');
};

const stageMinutes = (stage) => {
  const from = stage.duration_minutes;
  const to = stage.duration_to_minutes;
  if (from && to && Number(to) !== Number(from)) return `${from}–${to} min`;
  if (from || to) return `${from || to} min`;
  return null;
};

const stageToReadableLine = (stage) => [
  stage.name,
  stage.subject,
  stage.question_count ? `${stage.question_count} questions` : null,
  stageMinutes(stage),
].filter(Boolean).join(' · ');

const PERMISSIONS_UNGROUPED_LABEL = 'Other';
/** Must match backend `permissions.parent_panel_group_prefix` — parent subgroups are `Parent panel (…)`. */
const PARENT_PANEL_GROUP_PREFIX = 'Parent panel';
/** Must match backend `permissions.student_portal_group_prefix`. */
const STUDENT_PORTAL_GROUP_PREFIX = 'Student portal';
/** Must match backend `permissions.tutor_portal_group_prefix`. */
const TUTOR_PORTAL_GROUP_PREFIX = 'Tutor portal';
const ROLE_PARENT_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'parent', label: 'Parent' },
  { value: 'student', label: 'Student' },
  { value: 'tutor', label: 'Tutor' },
];
const ROLE_NAME_BY_PARENT_KEY = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  parent: 'Parent',
  student: 'Student',
  tutor: 'Tutor',
};

/**
 * Admin display label: `view-parents` → `View Parents` (split on -/_, title-case each part).
 * API still uses the raw `name` for checks and payloads.
 */
function formatPermissionDisplayName(name) {
  if (name == null || typeof name !== 'string') {
    return '';
  }
  return name
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function isParentPanelGroupLabel(name) {
  return name !== PERMISSIONS_UNGROUPED_LABEL && String(name).startsWith(PARENT_PANEL_GROUP_PREFIX);
}

function isStudentPortalGroupLabel(name) {
  return name !== PERMISSIONS_UNGROUPED_LABEL && String(name).startsWith(STUDENT_PORTAL_GROUP_PREFIX);
}

function isTutorPortalGroupLabel(name) {
  return name !== PERMISSIONS_UNGROUPED_LABEL && String(name).startsWith(TUTOR_PORTAL_GROUP_PREFIX);
}

/** Group permissions by `group` (API); unlabeled → "Other". Admin groups, then portal subgroups, then Other. */
function groupPermissionsByGroup(permissions) {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return [];
  }
  const map = new Map();
  for (const p of permissions) {
    const raw = p.group;
    const key =
      raw != null && String(raw).trim() !== ''
        ? String(raw).trim()
        : PERMISSIONS_UNGROUPED_LABEL;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(p);
  }
  const groupSortTier = (name) => {
    if (name === PERMISSIONS_UNGROUPED_LABEL) {
      return 2;
    }
    if (isParentPanelGroupLabel(name) || isStudentPortalGroupLabel(name) || isTutorPortalGroupLabel(name)) {
      return 1;
    }
    return 0;
  };
  return [...map.entries()].sort(([a], [b]) => {
    const ta = groupSortTier(a);
    const tb = groupSortTier(b);
    if (ta !== tb) {
      return ta - tb;
    }
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });
}

/**
 * Role dialog only: which permission rows to show based on base portal (parent panel, student portal, admin, etc.).
 * Does not control Master Data tabs — those use masterFormPermission / adminHasPermission.
 */
function normalizeRoleKey(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
}

function isParentPanelPermission(permission) {
  return String(permission?.name || '').toLowerCase().startsWith('parent-panel-');
}

function isStudentPortalPermission(permission) {
  return String(permission?.name || '').toLowerCase().startsWith('student-portal-');
}

function isTutorPortalPermission(permission) {
  return String(permission?.name || '').toLowerCase().startsWith('tutor-portal-');
}

// Entity configurations based on the complete API specifications
// 
// STATUS COLUMN LOGIC:
// - Entities WITH status field: schools, genders, regions, target_schools, days, months, years, exam_boards
//   → Show ONLY their own status column (no automatic Status column)
//   → Status values: 'active'/'inactive' for schools, 1/2/3 for others
//   → Deletion info shown below status chip when deleted_at exists
// 
// - Entities WITHOUT status field: formats, weekdays, academic_years, weeks  
//   → Show automatic Status column (Active/Deleted based on deleted_at)
//   → academic_years also hides Created At column
//
// - All entities show red border, background tint, and reduced opacity when deleted_at !== null
//
// Mirrors config/nvr_rules.php `subtypes` — the generator only understands these keys.
const NVR_SUBTYPE_OPTIONS = [
  { value: 'complete_the_series', label: 'Complete the Series' },
  { value: 'matrices', label: 'Matrices' },
  { value: 'analogies', label: 'Figure Analogies' },
  { value: 'odd_one_out', label: 'Odd One Out' },
  { value: 'find_like_first_two', label: 'Find the Like Figure (first two)' },
  { value: 'find_like_first_three', label: 'Find the Like Figure (first three)' },
  { value: 'congruent_figure', label: 'Congruent Figures' },
  { value: 'complete_the_shape', label: 'Complete the Shape' },
  { value: 'combining_shapes', label: 'Combining Shapes' },
  { value: 'hidden_shape', label: 'Hidden Shape' },
  { value: 'horizontal_code', label: 'Horizontal Codes' },
  { value: 'vertical_code', label: 'Vertical Codes' },
  { value: 'picture_sums', label: 'Picture Sums' },
  { value: 'mirror_image', label: 'Reflections' },
  { value: 'rotation', label: 'Rotations' },
  { value: 'paper_folding', label: 'Fold and Punch' },
  { value: 'combined_transform', label: 'Combined Transformations' },
  { value: 'decode_shapes', label: 'Decode Shapes' },
  { value: 'cubes_and_nets', label: 'Cube Nets' },
  { value: 'opposite_faces', label: 'Opposite Faces' },
  { value: 'cuboid_nets', label: 'Cuboid Nets' },
  { value: 'views_3d_2d', label: '2D Views of 3D Shapes' },
  { value: 'rotation_3d', label: '3D Rotation' },
  { value: 'cube_rotation', label: 'Cube Rotation' },
  { value: 'composite_shapes', label: 'Combining and Splitting Shapes' },
  { value: 'block_counting', label: 'Block Counting' },
  { value: 'shape_counting', label: 'Counting Shapes' },
];

const ENTITY_CONFIGS = {
  schools: { 
    name: 'Schools', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'School Name' },
      { name: 'target_school_id', type: 'select', required: true, label: 'School category' },
      { name: 'gender', type: 'select', required: false, label: 'Intake', options: [
        { value: 'boys', label: "Boys'" },
        { value: 'girls', label: "Girls'" },
        { value: 'co-ed', label: 'Co-educational' },
      ]},
      { name: 'region_id', type: 'select', required: false, label: 'Region' },
      { name: 'address', type: 'text', required: false, maxLength: 255, label: 'Address' },
      { name: 'phone', type: 'tel', required: false, maxLength: 255, label: 'Phone' },
      { name: 'email', type: 'email', required: false, maxLength: 255, label: 'Email' },
      { name: 'logo', type: 'text', required: false, maxLength: 255, label: 'Logo URL' },
      { name: 'website', type: 'url', required: false, maxLength: 255, label: 'Website' },
      { name: 'exam_board_id', type: 'select', required: false, label: 'Exam board (optional)' },
      { name: 'latitude', type: 'number', required: false, label: 'Latitude' },
      { name: 'longitude', type: 'number', required: false, label: 'Longitude' },
      { name: 'selection_type', type: 'select', required: false, label: 'Selection type', options: [
        { value: 'super_selective', label: 'Super-selective (pure merit)' },
        { value: 'consortium', label: 'Consortium (two-stage)' },
        { value: 'all_in_one', label: 'All-in-one (single day)' },
        { value: 'school_specific', label: 'School-specific' },
        { value: 'other', label: 'Other' },
      ]},
      { name: 'consortium_id', type: 'select', required: false, label: 'Consortium' },
      { name: 'exam_provider', type: 'text', required: false, maxLength: 120, label: 'Exam board / provider' },
      { name: 'question_style', type: 'text', required: false, maxLength: 120, label: 'Question style' },
      { name: 'exam_format', type: 'select', required: false, label: 'Exam format', options: [
        { value: 'mcq', label: 'MCQ' },
        { value: 'written', label: 'Written' },
        { value: 'mixed', label: 'Mixed' },
      ]},
      { name: 'exam_stages_text', type: 'textarea', required: false, label: 'Exam stages (one per line: Name | Subject | Questions | Minutes | To minutes)' },
      { name: 'reasoning_included', type: 'select', required: false, label: 'Reasoning included?', options: [
        { value: '1', label: 'Yes' },
        { value: '0', label: 'No' },
      ]},
      { name: 'subjects_tested', type: 'text', required: false, maxLength: 255, label: 'Subjects tested' },
      { name: 'entry_format', type: 'textarea', required: false, label: 'Entry format' },
      { name: 'pass_criteria', type: 'textarea', required: false, label: 'Pass criteria' },
      { name: 'entry_year', type: 'number', required: false, min: 2020, max: 2100, label: 'Admissions entry year (e.g. 2027)' },
      { name: 'open_day_date', type: 'date', required: false, label: 'Open day date' },
      { name: 'open_day_notes', type: 'text', required: false, maxLength: 255, label: 'Open day notes' },
      { name: 'exam_date', type: 'date', required: false, label: 'Exam date' },
      { name: 'exam_date_notes', type: 'text', required: false, maxLength: 255, label: 'Exam date notes' },
      { name: 'registration_deadline', type: 'date', required: false, label: 'Registration deadline' },
      { name: 'registration_notes', type: 'text', required: false, maxLength: 255, label: 'Registration notes' },
      { name: 'admission_notes', type: 'textarea', required: false, label: 'Admissions notes' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]}
    ]
  },
  roles: {
    name: 'Roles',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Role Name' },
      { name: 'parent_role', type: 'select', required: false, label: 'Base Role', options: ROLE_PARENT_OPTIONS }
    ]
  },
  genders: { 
    name: 'Genders', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 60, label: 'Gender Name' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  regions: { 
    name: 'Regions', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 100, label: 'Region Name' },
      { name: 'nation', type: 'text', required: false, maxLength: 40, label: 'Nation' },
      { name: 'selective', type: 'select', required: false, label: 'Selective 11+ area', options: [
        { value: 1, label: 'Yes' },
        { value: 0, label: 'No' },
      ]},
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  consortiums: {
    name: 'Consortiums',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 160, label: 'Consortium name' },
      { name: 'type', type: 'select', required: true, label: 'Type', options: [
        { value: 'grammar', label: 'Grammar' },
        { value: 'independent', label: 'Independent' },
        { value: 'council_test', label: 'Council test' },
      ]},
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ]},
    ],
  },
  // formats: { 
  //   name: 'Formats', 
  //   fields: [
  //     { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Format Name' },
  //     { name: 'description', type: 'textarea', required: false, label: 'Description' }
  //   ]
  // },
  target_schools: { 
    name: 'School Categories', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 20, label: 'Category Name' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  days: { 
    name: 'Days', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 2, label: 'Day Code (e.g., Mo, Tu)' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  months: { 
    name: 'Months', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 10, label: 'Month Name' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  weekdays: { 
    name: 'Weekdays', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Weekday Name' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  houses: {
    name: 'Houses',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'House name' },
      { name: 'key', type: 'text', required: false, maxLength: 64, label: 'Key (optional, unique)' },
      { name: 'sort_order', type: 'number', required: true, min: 0, label: 'Sort order (rotation)' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  years: { 
    name: 'Years', 
    fields: [
      { name: 'name', type: 'number', required: true, min: 1900, max: 2100, label: 'Year' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  locations: { 
    name: 'Locations', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Location Name' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  subjects: {
    name: 'Subjects',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Subject Name' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  topics: {
     name: 'Topics',
     fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Topic Name' },
      { name: 'subject_id', type: 'select', required: true, label: 'Subject' },
      { name: 'school_year_id', type: 'select', required: false, label: 'School year' },
      { name: 'course_id', type: 'select', required: false, label: 'Select course' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Active' },
        { value: 0, label: 'Inactive' }
      ]}
    ],
    type: 'topics'
  },
  sub_topics: { 
    name: 'Sub Topics', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Sub Topic Name' },
      { name: 'subject_id', type: 'select', required: true, label: 'Subject' },
      { name: 'school_year_id', type: 'select', required: false, label: 'School year' },
      { name: 'course_topic_id', type: 'select', required: true, label: 'Select Topic' },
      { name: 'course_id', type: 'select', required: false, label: 'Select course' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Active' },
        { value: 0, label: 'Inactive' }
      ]},
      { name: 'areas', type: 'textarea', required: false, label: 'Focus Areas (one per line)' },
      { name: 'sample_questions', type: 'textarea', required: false, label: 'Sample Questions / Instructions' },
    ],
    type: 'sub_topics'
  },
  categories: { 
    name: 'Paper/Mock Exam Categories', 
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Category Name' },
      { name: 'parent_id', type: 'select', required: false, label: 'Parent Category' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  academic_years: { 
    name: 'Academic Years', 
    fields: [
      { name: 'start_year', type: 'number', required: true, min: 1900, max: 2100, label: 'Start Year', placeholder: 'e.g., 2025' },
      { name: 'end_year', type: 'number', required: true, min: 1900, max: 2100, label: 'End Year', placeholder: 'e.g., 2026' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ],
    type: 'academic'
  },
  school_years: {
    name: 'School Years',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 20, label: 'Year Name (e.g., Year 3)' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ]
  },
  exam_boards: {
    name: 'Exam Boards',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Exam board name' },
      { name: 'code', type: 'text', required: false, maxLength: 32, label: 'Short code (e.g. GL)' },
      {
        name: 'status',
        type: 'select',
        required: false,
        label: 'Status',
        options: [
          { value: 1, label: 'Pending' },
          { value: 2, label: 'Approved' },
          { value: 3, label: 'Rejected' },
        ],
      },
    ],
  },
  weeks: {
    name: 'Weeks',
    fields: [
      { name: 'academic_year_id', type: 'select', required: true, label: 'Academic Year' },
      { name: 'week_number', type: 'text', required: true, maxLength: 2, label: 'Week Number', placeholder: 'e.g., 1' },
      { name: 'start_date', type: 'datetime-local', required: true, label: 'Start Date' },
      { name: 'end_date', type: 'datetime-local', required: true, label: 'End Date' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Rejected' }
      ]}
    ],
    type: 'weeks'
  },
  reference_books: {
    name: 'Reference Books',
    fields: [
      { name: 'title', type: 'text', required: true, maxLength: 255, label: 'Book Title' },
      { name: 'author', type: 'text', required: false, maxLength: 255, label: 'Author' },
      { name: 'genre', type: 'text', required: false, maxLength: 255, label: 'Genre(s)' },
      { name: 'subject', type: 'select', required: false, label: 'Subject', options: [
        { value: 'english', label: 'English' },
        { value: 'math', label: 'Math' },
        { value: 'verbal', label: 'Verbal Reasoning' },
        { value: 'non_verbal', label: 'Non-Verbal Reasoning' },
      ]},
      { name: 'published_date', type: 'date', required: false, label: 'Published Date' },
      { name: 'is_primary', type: 'checkbox', required: false, label: 'Primary Focus Book' },
      { name: 'pdf_file', type: 'file', required: false, label: 'Upload PDF', accept: '.pdf' },
      { name: 'epub_file', type: 'file', required: false, label: 'Upload EPUB', accept: '.epub' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Active' },
        { value: 0, label: 'Inactive' },
      ]},
    ],
  },
  nvr_rules: {
    name: 'NVR Rules',
    fields: [
      { name: 'topic_id', type: 'select', required: false, label: 'NVR topic' },
      { name: 'sub_topic_id', type: 'select', required: false, label: 'NVR sub-topic' },
      { name: 'subtype', type: 'select', required: true, label: 'Question subtype', options: NVR_SUBTYPE_OPTIONS },
      { name: 'rule_type', type: 'text', required: true, maxLength: 60, label: 'Rule type', placeholder: 'e.g., rotation_progression' },
      { name: 'difficulty', type: 'select', required: true, label: 'Difficulty', options: [
        { value: 'easy', label: 'Easy' },
        { value: 'medium', label: 'Medium' },
        { value: 'hard', label: 'Hard' },
      ]},
      { name: 'rule_configuration', type: 'textarea', required: true, label: 'Rule configuration (JSON)' },
      { name: 'sort_order', type: 'number', required: false, label: 'Sort order' },
      { name: 'status', type: 'select', required: false, label: 'Status', options: [
        { value: 1, label: 'Active' },
        { value: 0, label: 'Inactive' },
      ]},
    ],
  },
  mobile_subscription_plans: {
    name: 'Mobile Subscriptions',
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 120, label: 'Plan name' },
      { name: 'slug', type: 'text', required: false, maxLength: 120, label: 'Slug (auto from name if empty)', placeholder: 'e.g. standard-monthly' },
      { name: 'description', type: 'textarea', required: false, label: 'Description' },
      { name: 'price_pence', type: 'number', required: true, label: 'Price (pence)', placeholder: '999 = £9.99', helperText: 'Changing this opens a new price version. Existing subscribers keep the amount they joined at — see Price history on the Subscription plans page. Stripe prices are immutable, so set a new Stripe price ID too.' },
      { name: 'currency', type: 'text', required: false, maxLength: 3, label: 'Currency', placeholder: 'gbp' },
      {
        name: 'billing_interval',
        type: 'select',
        required: true,
        label: 'Time period',
        options: [
          { value: 'week', label: 'Weekly' },
          { value: '4 weeks', label: 'Every 4 weeks' },
          { value: 'month', label: 'Monthly' },
          { value: '2 months', label: 'Every 2 months' },
          { value: '3 months', label: 'Every 3 months' },
          { value: '4 months', label: 'Every 4 months' },
          { value: '6 months', label: 'Every 6 months' },
          { value: '8 months', label: 'Every 8 months' },
          { value: 'year', label: 'Yearly' },
          { value: '1 day', label: '1 day (trial)' },
          { value: '7 days', label: '7 days (trial)' },
          { value: 'one_time', label: 'One-time' },
        ],
      },
      { name: 'stripe_price_id', type: 'text', required: false, maxLength: 191, label: 'Stripe price ID', placeholder: 'price_...' },
      // Early-adopter offer. Applies to the Standard monthly plan only, and only while the
      // window is open; whoever subscribes inside it keeps the price until they cancel.
      { name: 'promo_price_pence', type: 'number', required: false, label: 'Early-adopter price (pence)', placeholder: '999 = £9.99', helperText: 'Standard monthly plan only. Leave blank for no offer.' },
      { name: 'promo_stripe_price_id', type: 'text', required: false, maxLength: 191, label: 'Early-adopter Stripe price ID', placeholder: 'price_...', helperText: 'Required — the discount cannot be charged without its own Stripe price.' },
      { name: 'promo_starts_at', type: 'datetime-local', required: false, label: 'Offer starts' },
      { name: 'promo_ends_at', type: 'datetime-local', required: false, label: 'Offer ends', helperText: 'After this, new customers pay the normal price. Existing early adopters keep theirs.' },
      { name: 'apple_product_id', type: 'text', required: false, maxLength: 128, label: 'Apple product ID' },
      { name: 'google_product_id', type: 'text', required: false, maxLength: 128, label: 'Google product ID' },
      { name: 'sort_order', type: 'number', required: false, label: 'Sort order' },
      {
        name: 'is_active',
        type: 'select',
        required: true,
        label: 'Status',
        options: [
          { value: '1', label: 'Active' },
          { value: '0', label: 'Inactive' },
        ],
      },
      {
        name: 'is_public',
        type: 'select',
        required: true,
        label: 'Visibility',
        helperText: 'Hidden plans are left out of every public plan list. Choose who can see one under Subscription plans → Allowed users.',
        options: [
          { value: '1', label: 'Visible to everyone' },
          { value: '0', label: 'Hidden (allowed users only)' },
        ],
      },
    ],
  },
  cancellation_reasons: {
    name: 'Cancellation Reasons',
    // Offered on the cancel-subscription screen in BOTH the parent portal and the mobile app; they
    // read the same list, so an edit here changes both. "Other" is not a row — each client appends
    // it as the free-text option, so it cannot be renamed or deleted here by accident.
    fields: [
      { name: 'name', type: 'text', required: true, maxLength: 255, label: 'Reason', placeholder: 'e.g. Too expensive' },
      { name: 'description', type: 'textarea', required: false, maxLength: 1000, label: 'Internal note (not shown to users)' },
      { name: 'sort_order', type: 'number', required: false, label: 'Sort order' },
      {
        name: 'status',
        type: 'select',
        required: false,
        label: 'Status',
        options: [
          // Retire rather than delete: a retired reason stops being offered but stays readable on
          // the subscriptions already cancelled with it.
          { value: 1, label: 'Offered' },
          { value: 0, label: 'Retired' },
        ],
      },
    ],
  },
};

// Dynamic validation schema generator based on field configurations
const getValidationSchema = (entityType, context = {}) => {
  const { roleEditingId = null } = context;
  const config = ENTITY_CONFIGS[entityType];
  if (!config) {
    return yup.object({
      name: yup.string().required('Name is required').max(255)
    });
  }

  const schemaFields = {};
  
  config.fields.forEach(field => {
    let validator;
    
    switch (field.type) {
      case 'number':
        validator = yup.number();
        if (!field.required) {
          validator = validator
            .transform((value, originalValue) =>
              originalValue === '' || originalValue == null || Number.isNaN(value) ? null : value
            )
            .nullable()
            .notRequired();
        } else {
          validator = validator.required(`${field.label} is required`);
        }
        if (field.min) validator = validator.min(field.min, `${field.label} must be at least ${field.min}`);
        if (field.max) validator = validator.max(field.max, `${field.label} must not exceed ${field.max}`);
        break;
        
      case 'email':
        validator = yup.string();
        if (!field.required) {
          validator = validator
            .transform((value, originalValue) =>
              originalValue === '' || originalValue == null ? null : value
            )
            .nullable()
            .notRequired();
        } else {
          validator = validator.required(`${field.label} is required`);
        }
        if (field.maxLength) validator = validator.max(field.maxLength, `${field.label} must not exceed ${field.maxLength} characters`);
        validator = validator.email('Must be a valid email address');
        break;
        
      case 'url':
        validator = yup.string();
        if (!field.required) {
          validator = validator
            .transform((value, originalValue) =>
              originalValue === '' || originalValue == null ? null : value
            )
            .nullable()
            .notRequired();
        } else {
          validator = validator.required(`${field.label} is required`);
        }
        if (field.maxLength) validator = validator.max(field.maxLength, `${field.label} must not exceed ${field.maxLength} characters`);
        validator = validator.url('Must be a valid URL');
        break;
        
      case 'select':
        if (field.options && field.options.length > 0) {
          const validValues = field.options.map(opt => opt.value);
          validator = yup.mixed();
          if (field.required) {
            validator = validator
              .required(`${field.label} is required`)
              .oneOf(validValues, `${field.label} must be one of: ${validValues.join(', ')}`);
          } else {
            validator = validator
              .transform((value, originalValue) =>
                originalValue === '' || originalValue == null ? null : value
              )
              .nullable()
              .notRequired()
              .oneOf([...validValues, null], `${field.label} must be one of: ${validValues.join(', ')}`);
          }
        } else {
          validator = yup.mixed();
          if (field.required) validator = validator.required(`${field.label} is required`);
        }
        break;
        
      case 'datetime-local':
      case 'date':
        validator = yup.string();
        if (field.required) validator = validator.required(`${field.label} is required`);
        break;

      case 'checkbox':
        validator = yup.boolean();
        break;

      case 'file':
        validator = yup.mixed();
        break;

      default: // text, textarea, tel
        validator = yup.string();
        if (field.required) validator = validator.required(`${field.label} is required`);
        if (field.maxLength) validator = validator.max(field.maxLength, `${field.label} must not exceed ${field.maxLength} characters`);
        break;
    }
    
    schemaFields[field.name] = validator;
  });

  if (entityType === 'roles') {
    const parentRoleKeys = ROLE_PARENT_OPTIONS.map((o) => o.value);
    const coreRoleNames = new Set(['super admin', 'admin', 'parent', 'student', 'tutor']);
    const normName = (s) => String(s ?? '').trim().toLowerCase();

    schemaFields.parent_role = yup
      .string()
      .nullable()
      .when('name', {
        is: (name) => coreRoleNames.has(normName(name)),
        then: (schema) =>
          schema.oneOf(['', ...parentRoleKeys], 'Invalid base role'),
        otherwise: (schema) =>
          schema
            .required('Base role is required')
            .oneOf(parentRoleKeys, `Base role must be one of: ${parentRoleKeys.join(', ')}`),
      });

    schemaFields.name = schemaFields.name.test(
      'unique-role-name',
      'A role with this name already exists',
      async function (value) {
        const trimmed = String(value ?? '').trim();
        if (!trimmed) {
          return true;
        }
        const editingId = roleEditingId != null ? Number(roleEditingId) : null;
        try {
          const res = await api.get('admin/rbac/roles');
          const roles = Array.isArray(res.data?.data) ? res.data.data : [];
          const n = normName(trimmed);
          const conflict = roles.some((r) => {
            if (normName(r.name) !== n) {
              return false;
            }
            if (editingId == null || Number.isNaN(editingId)) {
              return true;
            }
            return Number(r.id) !== editingId;
          });
          return !conflict;
        } catch {
          return true;
        }
      }
    );
  }

  // Special validation for academic years
  if (entityType === 'academic_years') {
    // Add custom validation for consecutive years
    schemaFields.end_year = schemaFields.end_year.test(
      'consecutive-years',
      'End year must be exactly one year after start year',
      function(value) {
        const startYear = this.parent.start_year;
        if (!startYear || !value) return true; // Let required validation handle empty values
        return parseInt(value) === parseInt(startYear) + 1;
      }
    );
  }
  
  // Special validation for weeks dates
  if (entityType === 'schools') {
    schemaFields.target_school_id = yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null ? undefined : Number(originalValue)
      )
      .typeError('School category is required')
      .required('School category is required')
      .integer('Select a valid school category')
      .positive('Select a valid school category');

    schemaFields.region_id = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test(
        'region-id',
        'Select a valid region',
        (value) => value == null || (Number.isInteger(value) && value > 0)
      );

    schemaFields.consortium_id = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test(
        'consortium-id',
        'Select a valid consortium',
        (value) => value == null || (Number.isInteger(value) && value > 0)
      );

    schemaFields.latitude = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test('latitude', 'Latitude must be between -90 and 90', (value) =>
        value == null || (typeof value === 'number' && !Number.isNaN(value) && value >= -90 && value <= 90)
      );

    schemaFields.longitude = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test('longitude', 'Longitude must be between -180 and 180', (value) =>
        value == null || (typeof value === 'number' && !Number.isNaN(value) && value >= -180 && value <= 180)
      );

    schemaFields.exam_board_id = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test(
        'exam-board-id',
        'Select a valid exam board',
        (value) =>
          value === null ||
          value === undefined ||
          (!Number.isNaN(Number(value)) && Number(value) > 0)
      );
  }

  if (entityType === 'weeks') {
    // Add custom validation for week_number (string that represents 1-52)
    schemaFields.week_number = schemaFields.week_number.test(
      'valid-week-number',
      'Week number must be between 1 and 52',
      function(value) {
        if (!value) return true; // Let required validation handle empty values
        const weekNum = parseInt(value);
        return !isNaN(weekNum) && weekNum >= 1 && weekNum <= 52;
      }
    );
    
    schemaFields.end_date = schemaFields.end_date.test(
      'after-start-date',
      'End date must be after start date',
      function(value) {
        if (!value || !this.parent.start_date) return true;
        return new Date(value) > new Date(this.parent.start_date);
      }
    );
  }

  if (entityType === 'topics' || entityType === 'sub_topics') {
    schemaFields.school_year_id = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test(
        'school-year-id',
        'Select a valid school year',
        (value) =>
          value === null ||
          value === undefined ||
          (!Number.isNaN(Number(value)) && Number(value) > 0)
      );

    schemaFields.course_id = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test(
        'course-id',
        'Select a valid course',
        (value) =>
          value === null ||
          value === undefined ||
          (!Number.isNaN(Number(value)) && Number(value) > 0)
      );
  }

  if (entityType === 'sub_topics') {
    schemaFields.subject_id = yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null ? undefined : Number(originalValue)
      )
      .typeError('Subject is required')
      .required('Subject is required')
      .integer('Select a valid subject')
      .positive('Select a valid subject');

    schemaFields.course_topic_id = yup
      .mixed()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null || originalValue === undefined
          ? null
          : Number(originalValue)
      )
      .nullable()
      .test(
        'parent-topic-id',
        'Select a valid parent topic',
        (value) =>
          value === null ||
          value === undefined ||
          (!Number.isNaN(Number(value)) && Number(value) > 0)
      );
  }

  
  return yup.object(schemaFields);
};

const SCHOOL_TABLE_FIELDS = [
  'name',
  'target_school_id',
  'gender',
  'region_id',
  'selection_type',
  'consortium_name',
  'exam_provider',
  'entry_year',
  'registration_deadline',
  'exam_date',
  'status',
];

const SCHOOL_GENDER_LABELS = {
  boys: "Boys'",
  girls: "Girls'",
  'co-ed': 'Co-educational',
};

const SCHOOL_SELECTION_TYPE_LABELS = {
  super_selective: 'Super-selective (pure merit)',
  consortium: 'Consortium (two-stage)',
  all_in_one: 'All-in-one (single day)',
  school_specific: 'School-specific',
  other: 'Other',
};

const SCHOOL_CSV_EXPORT_PAGE_SIZE = 2000;

const relatedName = (record, ...keys) => {
  for (const key of keys) {
    const name = record?.[key]?.name;
    if (name) return name;
  }
  return '';
};

const schoolAdmissionCycle = (record) =>
  record?.latest_admission_cycle || record?.latestAdmissionCycle || {};

const csvDate = (value) => (value ? String(value).slice(0, 10) : '');

const csvYesNo = (value) => {
  if (value === true || value === 1 || value === '1') return 'Yes';
  if (value === false || value === 0 || value === '0') return 'No';
  return '';
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const SCHOOL_CSV_COLUMNS = [
  { header: 'ID', value: (r) => r.id ?? '' },
  { header: 'School Name', value: (r) => r.name ?? '' },
  { header: 'Category', value: (r) => relatedName(r, 'target_school', 'targetSchool') },
  { header: 'Intake', value: (r) => SCHOOL_GENDER_LABELS[r.gender] ?? '' },
  { header: 'Region', value: (r) => relatedName(r, 'region') },
  { header: 'Country', value: (r) => relatedName(r, 'country') },
  { header: 'Address', value: (r) => r.address ?? '' },
  { header: 'Phone', value: (r) => r.phone ?? '' },
  { header: 'Email', value: (r) => r.email ?? '' },
  { header: 'Website', value: (r) => r.website ?? '' },
  { header: 'Logo URL', value: (r) => r.logo ?? '' },
  { header: 'Exam Board', value: (r) => relatedName(r, 'exam_board', 'examBoard') },
  {
    header: 'Exam Board Code',
    value: (r) => r.exam_board?.code || r.examBoard?.code || '',
  },
  { header: 'Latitude', value: (r) => r.latitude ?? '' },
  { header: 'Longitude', value: (r) => r.longitude ?? '' },
  {
    header: 'Selection Type',
    value: (r) => SCHOOL_SELECTION_TYPE_LABELS[r.selection_type] || r.selection_type || '',
  },
  { header: 'Consortium / Test Group', value: (r) => r.consortium_name ?? '' },
  { header: 'Exam Provider', value: (r) => r.exam_provider ?? '' },
  { header: 'Question Style', value: (r) => r.question_style ?? '' },
  { header: 'Reasoning Included', value: (r) => csvYesNo(r.reasoning_included) },
  { header: 'Subjects Tested', value: (r) => r.subjects_tested ?? '' },
  { header: 'Entry Format', value: (r) => r.entry_format ?? '' },
  { header: 'Pass Criteria', value: (r) => r.pass_criteria ?? '' },
  { header: 'Entry Year', value: (r) => schoolAdmissionCycle(r).entry_year ?? '' },
  { header: 'Open Day Date', value: (r) => csvDate(schoolAdmissionCycle(r).open_day_date) },
  { header: 'Open Day Notes', value: (r) => schoolAdmissionCycle(r).open_day_notes ?? '' },
  { header: 'Exam Date', value: (r) => csvDate(schoolAdmissionCycle(r).exam_date) },
  { header: 'Exam Date Notes', value: (r) => schoolAdmissionCycle(r).exam_date_notes ?? '' },
  { header: 'Registration Date', value: (r) => csvDate(schoolAdmissionCycle(r).registration_deadline) },
  { header: 'Registration Deadline', value: (r) => csvDate(schoolAdmissionCycle(r).registration_deadline) },
  { header: 'Registration Notes', value: (r) => schoolAdmissionCycle(r).registration_notes ?? '' },
  { header: 'Admissions Notes', value: (r) => schoolAdmissionCycle(r).notes ?? '' },
  { header: 'Status', value: (r) => r.status ?? '' },
  { header: 'Created At', value: (r) => csvDate(r.created_at) },
  { header: 'Updated At', value: (r) => csvDate(r.updated_at) },
];

const buildSchoolsCsv = (records) => {
  const headerLine = SCHOOL_CSV_COLUMNS.map((col) => csvCell(col.header)).join(',');
  const rows = records.map((record) =>
    SCHOOL_CSV_COLUMNS.map((col) => csvCell(col.value(record))).join(',')
  );
  return `\uFEFF${[headerLine, ...rows].join('\r\n')}`;
};

const downloadCsvFile = (filename, csvText) => {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const MOBILE_SUBSCRIPTION_TABLE_FIELDS = [
  'name',
  'slug',
  'price_pence',
  'currency',
  'billing_interval',
  'sort_order',
  'is_active',
  'is_public',
];

const MasterForm = () => {
  const [searchParams] = useSearchParams();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const canAccessEntityTab = (entity) =>
    adminHasAnyPermission([
      masterFormPermission('view', entity),
      masterFormPermission('create', entity),
      masterFormPermission('update', entity),
      masterFormPermission('delete', entity),
    ]);
  const canCreateEntity = (entity) => adminHasPermission(masterFormPermission('create', entity));
  const canUpdateEntity = (entity) => adminHasPermission(masterFormPermission('update', entity));
  const canDeleteEntity = (entity) => adminHasPermission(masterFormPermission('delete', entity));

  // State management
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [records, setRecords] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [academicYearsLoading, setAcademicYearsLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [topicsSubjectsList, setTopicsSubjectsList] = useState([]);
  const [topicsDropdownsLoading, setTopicsDropdownsLoading] = useState(false);
  const [subTopicsTopicsList, setSubTopicsTopicsList] = useState([]);
  const [subTopicsTopicsLoading, setSubTopicsTopicsLoading] = useState(false);
  const [nvrTopicsList, setNvrTopicsList] = useState([]);
  const [nvrSubTopicsList, setNvrSubTopicsList] = useState([]);
  const [nvrTopicsLoading, setNvrTopicsLoading] = useState(false);
  const [schoolYearsForTopicsList, setSchoolYearsForTopicsList] = useState([]);
  const [schoolYearsForTopicsLoading, setSchoolYearsForTopicsLoading] = useState(false);
  const [coursesForTopicsList, setCoursesForTopicsList] = useState([]);
  const [coursesForTopicsLoading, setCoursesForTopicsLoading] = useState(false);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [schoolCategoriesList, setSchoolCategoriesList] = useState([]);
  const [schoolCategoriesLoading, setSchoolCategoriesLoading] = useState(false);
  const [regionsList, setRegionsList] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [consortiumsList, setConsortiumsList] = useState([]);
  const [consortiumsLoading, setConsortiumsLoading] = useState(false);
  const [schoolListFilters, setSchoolListFilters] = useState({
    region_id: '',
    target_school_id: '',
    gender: '',
    selection_type: '',
    consortium_name: '',
  });
  const [schoolConsortiumOptions, setSchoolConsortiumOptions] = useState([]);
  const [enrichRunId, setEnrichRunId] = useState(null);
  const [enrichReviewOpen, setEnrichReviewOpen] = useState(false);
  const [enrichBulkBusy, setEnrichBulkBusy] = useState(false);
  const [enrichSingleBusy, setEnrichSingleBusy] = useState(false);
  const [triageOpen, setTriageOpen] = useState(false);
  const [schoolRegionSummary, setSchoolRegionSummary] = useState([]);
  const [exportingSchoolsCsv, setExportingSchoolsCsv] = useState(false);
  const [examBoardsList, setExamBoardsList] = useState([]);
  const [examBoardsLoading, setExamBoardsLoading] = useState(false);
  const [permissionsList, setPermissionsList] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  /** Set when GET rbac/permissions fails (404/network) — distinct from empty DB list */
  const [permissionsLoadError, setPermissionsLoadError] = useState(null);
  /** View Role: permissions from GET rbac/roles/:id (null = still loading) */
  const [viewRolePermissionsSnapshot, setViewRolePermissionsSnapshot] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [restoringRecordId, setRestoringRecordId] = useState(null);
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    severity: 'warning'
  });
  const [submitting, setSubmitting] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [epubFile, setEpubFile] = useState(null);
  const [bookViewer, setBookViewer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  /** Reference Books list filters. */
  const [refBookFilters, setRefBookFilters] = useState({ subject: '' });
  /** Topics / Sub Topics list filters (avoid generic name `filters` — HMR / scope confusion). */
  const [topicListFilters, setTopicListFilters] = useState({
    subject_id: '',
    school_year_id: '',
    status: '',
    parent_topic_id: '',
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(15);
  // Entity selection tab (within Master Data)
  const [tabValue, setTabValue] = useState(0);

  const getTableFields = useCallback((entity) => {
    const config = ENTITY_CONFIGS[entity];
    if (!config) return [];
    if (entity === 'reference_books') {
      return config.fields.filter((field) => field.name !== 'epub_file');
    }
    if (entity === 'schools') {
      return config.fields.filter((field) => SCHOOL_TABLE_FIELDS.includes(field.name));
    }
    if (entity === 'mobile_subscription_plans') {
      return config.fields.filter((field) => MOBILE_SUBSCRIPTION_TABLE_FIELDS.includes(field.name));
    }
    if (entity === 'sub_topics') {
      return config.fields.filter(
        (field) => field.name !== 'areas' && field.name !== 'sample_questions'
      );
    }
    if (entity === 'nvr_rules') {
      return config.fields.filter((field) => field.name !== 'rule_configuration');
    }
    return config.fields;
  }, []);

  const rolesValidationContext = useMemo(
    () =>
      selectedEntity === 'roles' && editMode && currentRecord?.id != null
        ? { roleEditingId: currentRecord.id }
        : { roleEditingId: null },
    [selectedEntity, editMode, currentRecord?.id]
  );

  const entityValidationSchema = useMemo(
    () =>
      selectedEntity
        ? getValidationSchema(
            selectedEntity,
            selectedEntity === 'roles' ? rolesValidationContext : {}
          )
        : yup.object(),
    [selectedEntity, rolesValidationContext]
  );

  // Form setup
  const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(entityValidationSchema),
    defaultValues: {
      permission_ids: [],
      parent_role: '',
    },
  });
  const watchedSchoolYearId = watch('school_year_id');
  const watchedSubjectId = watch('subject_id');
  const selectedParentRole = String(watch('parent_role') || '').toLowerCase();
  const roleNameWatch = String(watch('name') || '').trim().toLowerCase();
  const isParentRoleName = ['super admin', 'admin', 'parent', 'student', 'tutor'].includes(roleNameWatch);
  const effectiveRoleScope = normalizeRoleKey(selectedParentRole || roleNameWatch);
  const roleScopedPermissions = useMemo(() => {
    if (!Array.isArray(permissionsList) || permissionsList.length === 0) {
      return [];
    }
    if (effectiveRoleScope === 'parent') {
      return permissionsList.filter((permission) => isParentPanelPermission(permission));
    }
    if (effectiveRoleScope === 'student') {
      return permissionsList.filter((permission) => isStudentPortalPermission(permission));
    }
    if (effectiveRoleScope === 'tutor') {
      return permissionsList.filter((permission) => isTutorPortalPermission(permission));
    }
    if (['admin', 'super_admin'].includes(effectiveRoleScope)) {
      return permissionsList.filter(
        (permission) =>
          !isParentPanelPermission(permission) &&
          !isStudentPortalPermission(permission) &&
          !isTutorPortalPermission(permission)
      );
    }
    return permissionsList;
  }, [permissionsList, effectiveRoleScope]);

  const isTopicsReorderMode = Boolean(
    selectedEntity === 'topics' &&
    topicListFilters.school_year_id
  );

  const isSubTopicsReorderMode = Boolean(
    selectedEntity === 'sub_topics' &&
    topicListFilters.school_year_id &&
    topicListFilters.parent_topic_id
  );

  const resolveCatalogueTopicId = (record) =>
    record?.id ?? record?.topic?.id ?? record?.topic_id ?? null;

  const resolveParentCatalogueTopicId = () => {
    const v = topicListFilters.parent_topic_id;
    return v ? Number(v) : null;
  };

  /**
   * Deduplicate `subTopicsTopicsList` by master topic id. Each `course_topics`
   * row points to a master `topic` row; multiple course_topics rows can share
   * the same master topic id (one per course_assignment), causing visual
   * duplicates in the dropdowns. We collapse them to one option per master
   * topic, keeping a sample course_topic id for legacy create/edit submits.
   */
  const dedupedSubTopicsTopicsList = useMemo(() => {
    const seen = new Map();
    for (const row of subTopicsTopicsList) {
      const masterId = row?.id ?? row?.topic?.id ?? row?.topic_id;
      if (!masterId) continue;
      const key = String(masterId);
      if (seen.has(key)) continue;
      seen.set(key, {
        master_topic_id: Number(masterId),
        name: row?.name ?? row?.topic?.name,
        subject: row?.subject || row?.topic?.subject,
        sample_course_topic_id: row?.id,
      });
    }
    return sortHierarchyItems(Array.from(seen.values()));
  }, [subTopicsTopicsList]);

  const uniqueOrderedIds = (ids) => {
    const seen = new Set();
    const out = [];
    for (const id of ids) {
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(Number(id));
    }
    return out;
  };

  const handleReorderDragEnd = async (event) => {
    const { active, over } = event || {};
    if (!active?.id || !over?.id || String(active.id) === String(over.id)) return;
    if (reorderBusy) return;

    const items = records.map((r) => String(r.id));
    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const nextRecords = arrayMove(records, oldIndex, newIndex);
    const prevRecords = records;
    setRecords(nextRecords);
    setReorderBusy(true);

    try {
      if (isTopicsReorderMode) {
        const orderedTopicIds = nextRecords
          .map((r) => resolveCatalogueTopicId(r))
          .filter((id) => id != null)
          .map((id) => Number(id));
        const uniqueTopicIds = uniqueOrderedIds(orderedTopicIds);
        await api.patch('admin/master-form/topics/reorder', {
          subject_id: topicListFilters.subject_id ? Number(topicListFilters.subject_id) : null,
          school_year_id: Number(topicListFilters.school_year_id),
          ordered_topic_ids: uniqueTopicIds,
        });
        toast.success('Topic order saved');
      } else if (isSubTopicsReorderMode) {
        const parentTopicId = resolveParentCatalogueTopicId();
        if (!parentTopicId) {
          throw new Error('Missing parent topic');
        }
        const orderedSubtopicIds = nextRecords
          .map((r) => resolveCatalogueTopicId(r))
          .filter((id) => id != null)
          .map((id) => Number(id));
        const uniqueSubtopicIds = uniqueOrderedIds(orderedSubtopicIds);
        await api.patch('admin/master-form/sub-topics/reorder', {
          parent_topic_id: Number(parentTopicId),
          subject_id: topicListFilters.subject_id ? Number(topicListFilters.subject_id) : null,
          school_year_id: Number(topicListFilters.school_year_id),
          ordered_subtopic_ids: uniqueSubtopicIds,
        });
        toast.success('Sub-topic order saved');
      }
    } catch (e) {
      setRecords(prevRecords);
      const backendMessage =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (e?.response?.data?.errors
          ? Object.values(e.response.data.errors).flat().join(' ')
          : '');
      toast.error(backendMessage || 'Failed to save order');
      // Keep a console breadcrumb with payload/debug context for quick diagnosis.
      console.error('Failed to save reorder', {
        entity: selectedEntity,
        isTopicsReorderMode,
        isSubTopicsReorderMode,
        filters: topicListFilters,
        error: e?.response?.data || e?.message || e,
      });
    } finally {
      setReorderBusy(false);
    }
  };

  // Helper function to show confirmation dialog
  const showConfirmDialog = (title, message, onConfirm, options = {}) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      severity: options.severity || 'warning'
    });
  };

  // Helper function to close confirmation dialog
  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false, onConfirm: null }));
  };

  // Fetch available entities on component mount
  useEffect(() => {
    fetchEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Fetch academic years for weeks entity
  useEffect(() => {
    if (selectedEntity === 'weeks') {
      fetchAcademicYears();
    }
  }, [selectedEntity]);

  // Fetch categories for categories entity (parent dropdown)
  useEffect(() => {
    if (selectedEntity === 'categories') {
      fetchCategories();
    }
  }, [selectedEntity]);

  // Fetch subjects and academic courses for topics entity
  useEffect(() => {
    if (selectedEntity === 'topics') {
      fetchTopicsDropdowns();
      fetchSchoolYearsForTopics();
    }
  }, [selectedEntity]);

  // Fetch topics list for sub_topics entity (parent topic dropdown)
  useEffect(() => {
    if (selectedEntity === 'sub_topics') {
      fetchSchoolYearsForTopics();
      fetchTopicsDropdowns();
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (selectedEntity !== 'sub_topics') {
      return;
    }
    fetchSubTopicsTopicsList(watchedSubjectId || null, watchedSchoolYearId || null);
  }, [selectedEntity, watchedSubjectId, watchedSchoolYearId]);

  useEffect(() => {
    if (selectedEntity !== 'sub_topics') {
      return;
    }
    const sid = topicListFilters.subject_id || null;
    const syid = topicListFilters.school_year_id || null;
    if (!sid) {
      setSubTopicsTopicsList([]);
      setTopicListFilters((prev) => (
        prev.parent_topic_id ? { ...prev, parent_topic_id: '' } : prev
      ));
      return;
    }
    fetchSubTopicsTopicsList(sid, syid);
  }, [selectedEntity, topicListFilters.subject_id, topicListFilters.school_year_id]);

  useEffect(() => {
    if (selectedEntity !== 'topics' && selectedEntity !== 'sub_topics') {
      return;
    }
    if (!watchedSchoolYearId || watchedSchoolYearId === '') {
      setCoursesForTopicsList([]);
      return;
    }
    fetchCoursesForTopics(watchedSchoolYearId);
  }, [selectedEntity, watchedSchoolYearId]);

  // Target school categories (`target_schools` table) for Schools master form
  useEffect(() => {
    if (selectedEntity === 'schools') {
      fetchSchoolCategoriesList();
      fetchExamBoardsList();
      fetchRegionsList();
      fetchConsortiumsList();
      fetchSchoolRegionSummary();
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (selectedEntity === 'roles') {
      fetchPermissions();
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (selectedEntity === 'nvr_rules') {
      fetchNvrTopicsList();
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (selectedEntity === 'roles' && isParentRoleName) {
      setValue('parent_role', '');
    }
  }, [selectedEntity, isParentRoleName, setValue]);

  useEffect(() => {
    if (selectedEntity !== 'roles' || viewMode) {
      return;
    }
    const allowedIds = new Set(roleScopedPermissions.map((permission) => Number(permission.id)));
    const currentIds = Array.isArray(watch('permission_ids')) ? watch('permission_ids') : [];
    const sanitizedIds = currentIds
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && allowedIds.has(id));

    if (sanitizedIds.length !== currentIds.length) {
      setValue('permission_ids', sanitizedIds, { shouldValidate: false, shouldDirty: true });
    }
  }, [selectedEntity, viewMode, roleScopedPermissions, watch, setValue]);

  const fetchEntities = async () => {
    try {
      setEntitiesLoading(true);
      const response = await api.get('admin/master-form/entities');
      if (response.data.success) {
        // Keep all entities except formats (intentionally hidden in UI) and roles
        // (roles now live in the Roles & Permissions section at /admin/roles).
        const filteredEntities = response.data.data.filter(entity => entity !== 'formats' && entity !== 'roles');
        setEntities(filteredEntities);
        if (filteredEntities.length > 0) {
          const entityFromUrl = searchParams.get('entity');
          const initialEntity =
            entityFromUrl && filteredEntities.includes(entityFromUrl)
              ? entityFromUrl
              : filteredEntities[0];
          setSelectedEntity(initialEntity);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access master form entities.');
      } else {
        toast.error('Failed to fetch entities');
      }
      console.error('Error fetching entities:', error);
    } finally {
      setEntitiesLoading(false);
    }
  };

  const fetchRecords = useCallback(async () => {
    if (!selectedEntity) return;
    
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        include_trashed: selectedEntity === 'roles' ? true : includeDeleted,
        search: debouncedSearchTerm
      };
      
      // Try to include academic year relationship for weeks
      if (selectedEntity === 'weeks') {
        params.with = 'academic_year';
      }
      if (selectedEntity === 'topics') {
        params.with = 'subject,schoolYear,course';
      }
      if (selectedEntity === 'sub_topics') {
        params.with = 'subject,schoolYear,course,parent';
      }

      if (selectedEntity === 'reference_books') {
        if (refBookFilters.subject) params.subject = refBookFilters.subject;
      }

      // Add filters for topics and sub_topics
      if (selectedEntity === 'topics' || selectedEntity === 'sub_topics') {
        if (topicListFilters.subject_id) params.subject_id = topicListFilters.subject_id;
        if (topicListFilters.school_year_id) params.school_year_id = topicListFilters.school_year_id;
        if (selectedEntity === 'sub_topics' && topicListFilters.parent_topic_id) {
          params.parent_topic_id = topicListFilters.parent_topic_id;
        }
        if (topicListFilters.status !== undefined && topicListFilters.status !== '') {
          params.status = topicListFilters.status;
        }
      }

      if (selectedEntity === 'schools') {
        if (schoolListFilters.region_id) params.region_id = schoolListFilters.region_id;
        if (schoolListFilters.target_school_id) params.target_school_id = schoolListFilters.target_school_id;
        if (schoolListFilters.gender) params.gender = schoolListFilters.gender;
        if (schoolListFilters.selection_type) params.selection_type = schoolListFilters.selection_type;
        if (schoolListFilters.consortium_name) params.consortium_name = schoolListFilters.consortium_name;
      }

      const response = await api.get(`admin/master-form/${selectedEntity}`, { params });
      if (response.data.success) {
        const recordsData = response.data.data.data || [];
        setRecords(sortHierarchyItems(recordsData));
        setTotalPages(response.data.data.last_page || 1);
        setTotalRecords(response.data.data.total || 0);
      }
    } catch (error) {
      toast.error(`Failed to fetch ${ENTITY_CONFIGS[selectedEntity]?.name || selectedEntity}`);
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, currentPage, perPage, includeDeleted, debouncedSearchTerm, topicListFilters, refBookFilters, schoolListFilters]);

  /**
   * Research every school currently matching the list filters. The work happens on a
   * queue, so this only starts the run — the review dialog polls it and shows the diff.
   */
  const startBulkEnrichment = useCallback(async () => {
    setEnrichBulkBusy(true);
    try {
      const payload = {};
      if (schoolListFilters.region_id) payload.region_id = schoolListFilters.region_id;
      if (schoolListFilters.target_school_id) payload.target_school_id = schoolListFilters.target_school_id;
      if (schoolListFilters.gender) payload.gender = schoolListFilters.gender;
      if (schoolListFilters.selection_type) payload.selection_type = schoolListFilters.selection_type;
      if (schoolListFilters.consortium_name) payload.consortium_name = schoolListFilters.consortium_name;
      if (debouncedSearchTerm) payload.search = debouncedSearchTerm;

      const response = await api.post('admin/master-form/schools/enrich-bulk', payload);
      const runId = response.data?.data?.run?.id;
      if (!runId) {
        toast.error('The enrichment run could not be started');
        return;
      }
      toast.info(response.data?.message || 'Enrichment run queued');
      setEnrichRunId(runId);
      setEnrichReviewOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not start the enrichment run');
    } finally {
      setEnrichBulkBusy(false);
    }
  }, [schoolListFilters, debouncedSearchTerm]);

  /** Research one school and open the review dialog on the result. */
  const enrichSingleSchool = useCallback(async (schoolId, entryYear) => {
    if (!schoolId) return;
    setEnrichSingleBusy(true);
    try {
      const response = await api.post(`admin/master-form/schools/${schoolId}/enrich`, {
        ...(entryYear ? { entry_year: entryYear } : {}),
      });
      const runId = response.data?.data?.run?.id;
      const proposedCount = Object.keys(response.data?.data?.result?.proposed || {}).length;

      if (!response.data?.success) {
        toast.warning(response.data?.message || 'Could not research this school');
        return;
      }
      if (proposedCount === 0) {
        toast.info(response.data?.message || 'Nothing new found for this school');
        return;
      }

      setEnrichRunId(runId);
      setEnrichReviewOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not research this school');
    } finally {
      setEnrichSingleBusy(false);
    }
  }, []);

  const exportSchoolsCsv = useCallback(async () => {
    setExportingSchoolsCsv(true);
    try {
      const allRecords = [];
      let page = 1;
      let lastPage = 1;

      do {
        const params = {
          page,
          per_page: SCHOOL_CSV_EXPORT_PAGE_SIZE,
          include_trashed: includeDeleted,
          search: debouncedSearchTerm,
        };
        if (schoolListFilters.region_id) {
          params.region_id = schoolListFilters.region_id;
        }
        if (schoolListFilters.target_school_id) {
          params.target_school_id = schoolListFilters.target_school_id;
        }
        if (schoolListFilters.gender) {
          params.gender = schoolListFilters.gender;
        }
        if (schoolListFilters.selection_type) {
          params.selection_type = schoolListFilters.selection_type;
        }
        if (schoolListFilters.consortium_name) {
          params.consortium_name = schoolListFilters.consortium_name;
        }

        const response = await api.get('admin/master-form/schools', { params });
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Failed to fetch schools');
        }

        const payload = response.data.data || {};
        allRecords.push(...(payload.data || []));
        lastPage = payload.last_page || 1;
        page += 1;
      } while (page <= lastPage);

      if (allRecords.length === 0) {
        toast.warning('No schools to export');
        return;
      }

      const dateStamp = new Date().toISOString().slice(0, 10);
      downloadCsvFile(`schools-${dateStamp}.csv`, buildSchoolsCsv(allRecords));
      toast.success(`Exported ${allRecords.length} school${allRecords.length === 1 ? '' : 's'}`);
    } catch (error) {
      console.error('Error exporting schools CSV:', error);
      toast.error('Failed to export schools CSV');
    } finally {
      setExportingSchoolsCsv(false);
    }
  }, [includeDeleted, debouncedSearchTerm, schoolListFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEntity, debouncedSearchTerm, topicListFilters, refBookFilters, schoolListFilters]);

  // Fetch records when entity or pagination changes
  useEffect(() => {
    if (selectedEntity) {
      fetchRecords();
    }
  }, [selectedEntity, currentPage, debouncedSearchTerm, fetchRecords]);

  const fetchAcademicYears = async () => {
    try {
      setAcademicYearsLoading(true);
      const response = await api.get('admin/master-form/academic_years', {
        params: { per_page: 100 } // Get more records to ensure we have all academic years
      });
      if (response.data.success) {
        const academicYearData = response.data.data.data || [];
        setAcademicYears(academicYearData);
        console.log('Academic years loaded:', academicYearData); // Debug log
        
        if (academicYearData.length === 0) {
          toast.warning('No academic years found. Please create academic years first.');
        }
      }
    } catch (error) {
      console.error('Error fetching academic years:', error);
      toast.error('Failed to fetch academic years');
    } finally {
      setAcademicYearsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await api.get('admin/master-form/categories', {
        params: { per_page: 500 }
      });
      if (response.data.success) {
        const list = response.data.data.data || response.data.data || [];
        setCategoriesList(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchTopicsDropdowns = async () => {
    try {
      setTopicsDropdownsLoading(true);
      const subjectsRes = await api.get('admin/master-form/subjects', { params: { per_page: 500 } });
      if (subjectsRes.data?.success && subjectsRes.data?.data) {
        const list = subjectsRes.data.data.data || subjectsRes.data.data || [];
        setTopicsSubjectsList(sortHierarchyItems(Array.isArray(list) ? list : []));
      }
    } catch (error) {
      console.error('Error fetching topics dropdowns:', error);
      toast.error('Failed to load topics dropdowns');
    } finally {
      setTopicsDropdownsLoading(false);
    }
  };

  const fetchSubTopicsTopicsList = async (subjectId, schoolYearId = null) => {
    if (!subjectId) {
      setSubTopicsTopicsList([]);
      return;
    }
    try {
      setSubTopicsTopicsLoading(true);
      const params = { per_page: 500, subject_id: subjectId, status: 1 };
      if (schoolYearId) {
        params.school_year_id = schoolYearId;
      }
      const res = await api.get('admin/master-form/topics', { params });
      if (res.data?.success && res.data?.data) {
        const list = res.data.data.data || res.data.data || [];
        setSubTopicsTopicsList(sortHierarchyItems(Array.isArray(list) ? list : []));
      }
    } catch (error) {
      console.error('Error fetching topics for sub topics:', error);
      toast.error('Failed to load topics');
    } finally {
      setSubTopicsTopicsLoading(false);
    }
  };

  const fetchNvrTopicsList = async () => {
    try {
      setNvrTopicsLoading(true);
      const res = await api.get('admin/master-form/nvr-topics');
      setNvrTopicsList(Array.isArray(res.data?.data?.topics) ? res.data.data.topics : []);
      setNvrSubTopicsList(Array.isArray(res.data?.data?.sub_topics) ? res.data.data.sub_topics : []);
    } catch (error) {
      console.error('Error fetching NVR topics:', error);
      toast.error('Failed to load topics for NVR rules');
      setNvrTopicsList([]);
      setNvrSubTopicsList([]);
    } finally {
      setNvrTopicsLoading(false);
    }
  };

  const fetchSchoolYearsForTopics = async () => {
    try {
      setSchoolYearsForTopicsLoading(true);
      const res = await api.get('admin/master-form/school-years');
      if (res.data?.success && res.data?.data) {
        setSchoolYearsForTopicsList(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (error) {
      console.error('Error fetching school years:', error);
      toast.error('Failed to load school years');
    } finally {
      setSchoolYearsForTopicsLoading(false);
    }
  };

  const fetchCoursesForTopics = async (schoolYearId) => {
    if (!schoolYearId) {
      setCoursesForTopicsList([]);
      return;
    }
    try {
      setCoursesForTopicsLoading(true);
      const res = await api.get('admin/master-form/courses-for-topics', {
        params: { school_year_id: schoolYearId },
      });
      if (res.data?.success && res.data?.data) {
        setCoursesForTopicsList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setCoursesForTopicsList([]);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
      setCoursesForTopicsList([]);
    } finally {
      setCoursesForTopicsLoading(false);
    }
  };

  const fetchExamBoardsList = async () => {
    try {
      setExamBoardsLoading(true);
      const res = await api.get('admin/master-form/exam_boards', { params: { per_page: 500 } });
      if (res.data?.success && res.data?.data) {
        const list = res.data.data.data || res.data.data || [];
        setExamBoardsList(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('Error fetching exam boards:', error);
      toast.error('Failed to load exam boards');
      setExamBoardsList([]);
    } finally {
      setExamBoardsLoading(false);
    }
  };

  const fetchSchoolCategoriesList = async () => {
    try {
      setSchoolCategoriesLoading(true);
      const res = await api.get('admin/master-form/target_schools', { params: { per_page: 500 } });
      if (res.data?.success && res.data?.data) {
        const list = res.data.data.data || res.data.data || [];
        setSchoolCategoriesList(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('Error fetching target schools for school category:', error);
      toast.error('Failed to load school categories');
    } finally {
      setSchoolCategoriesLoading(false);
    }
  };

  const fetchRegionsList = async () => {
    try {
      setRegionsLoading(true);
      const res = await api.get('admin/master-form/regions', { params: { per_page: 500 } });
      if (res.data?.success && res.data?.data) {
        const list = res.data.data.data || res.data.data || [];
        setRegionsList(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error('Failed to load regions');
      setRegionsList([]);
    } finally {
      setRegionsLoading(false);
    }
  };

  const fetchConsortiumsList = async () => {
    try {
      setConsortiumsLoading(true);
      const res = await api.get('admin/school-data/options');
      const list = res.data?.data?.consortiums || [];
      setConsortiumsList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching consortiums:', error);
      setConsortiumsList([]);
    } finally {
      setConsortiumsLoading(false);
    }
  };

  const fetchSchoolRegionSummary = async () => {
    try {
      const res = await api.get('admin/master-form/schools/region-summary');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setSchoolRegionSummary(res.data.data);
        setSchoolConsortiumOptions(Array.isArray(res.data.consortiums) ? res.data.consortiums : []);
      } else {
        setSchoolRegionSummary([]);
        setSchoolConsortiumOptions([]);
      }
    } catch (error) {
      console.error('Error fetching school region summary:', error);
      setSchoolRegionSummary([]);
      setSchoolConsortiumOptions([]);
    }
  };

  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      setPermissionsLoadError(null);
      const res = await api.get('admin/rbac/permissions', { params: { active_only: 1 } });
      if (res.data?.success) {
        setPermissionsList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setPermissionsList([]);
        setPermissionsLoadError(res.data?.message || 'Could not load permissions.');
      }
    } catch (error) {
      setPermissionsList([]);
      const msg =
        error.response?.status === 404
          ? 'Permissions API not found (404). Deploy backend routes or clear route cache on the server.'
          : error.response?.data?.message || error.message || 'Request failed';
      setPermissionsLoadError(msg);
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const applyParentRolePermissions = async (parentRoleKey) => {
    if (!parentRoleKey) {
      setValue('permission_ids', [], { shouldValidate: false, shouldDirty: true });
      return;
    }
    try {
      const targetRoleName = ROLE_NAME_BY_PARENT_KEY[parentRoleKey];
      if (!targetRoleName) {
        return;
      }
      const res = await api.get('admin/rbac/roles');
      const roles = Array.isArray(res.data?.data) ? res.data.data : [];
      const parentRole = roles.find((r) => String(r?.name || '').toLowerCase() === targetRoleName.toLowerCase());
      const permissionIds = Array.isArray(parentRole?.permissions)
        ? parentRole.permissions.map((p) => Number(p.id)).filter((id) => !Number.isNaN(id))
        : [];
      setValue('permission_ids', permissionIds, { shouldValidate: false, shouldDirty: true });
    } catch (error) {
      console.error('Error loading parent role permissions:', error);
      toast.error('Failed to auto-load parent role permissions');
    }
  };

  // Calculate week dates based on academic year and week number
  const calculateWeekDates = async (academicYearId, weekNumber) => {
    try {
      // Find the selected academic year
      const selectedAcademicYear = academicYears.find(year => year.id === parseInt(academicYearId));
      if (!selectedAcademicYear || !selectedAcademicYear.start_end_year) {
        console.log('Academic year not found or missing start_end_year');
        return;
      }

      // Parse the academic year to get start and end years
      const [startYear] = selectedAcademicYear.start_end_year.split('/').map(Number);
      
      // Academic year typically starts in June and ends in May of next year
      // For simplicity, let's assume it starts on June 1st
      const academicYearStart = new Date(startYear, 5, 1); // June 1st (month 5 = June)
      
      // Calculate the start date of the specified week
      // Week 1 starts on the first Monday of the academic year
      const firstMonday = new Date(academicYearStart);
      const dayOfWeek = firstMonday.getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      // Fix Monday calculation logic
      let daysToAdd;
      if (dayOfWeek === 1) {
        // Already Monday
        daysToAdd = 0;
      } else if (dayOfWeek === 0) {
        // Sunday, add 1 day to get to Monday
        daysToAdd = 1;
      } else {
        // Tuesday-Saturday, calculate days to next Monday
        daysToAdd = 8 - dayOfWeek;
      }
      
      firstMonday.setDate(firstMonday.getDate() + daysToAdd);
      
      // Calculate the start date for the specified week number
      const weekStartDate = new Date(firstMonday);
      weekStartDate.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
      weekStartDate.setHours(0, 0, 0, 0); // Set to 00:00:00 for Monday start
      
      // Calculate the end date (Sunday at 22:00 as per backend requirements)
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6); // Add 6 days to get to Sunday
      weekEndDate.setHours(22, 0, 0, 0); // Set to 22:00:00
      
      // Debug logging to verify Monday calculation
      if (process.env.NODE_ENV === 'development') {
        console.log('Week calculation debug:', {
          academicYear: selectedAcademicYear.start_end_year,
          academicYearStart: academicYearStart.toDateString(),
          firstMonday: firstMonday.toDateString(),
          weekNumber: weekNumber,
          weekStartDate: weekStartDate.toDateString() + ' ' + weekStartDate.toTimeString(),
          weekEndDate: weekEndDate.toDateString() + ' ' + weekEndDate.toTimeString(),
          startDayOfWeek: weekStartDate.getDay(), // Should be 1 (Monday)
          endDayOfWeek: weekEndDate.getDay() // Should be 0 (Sunday)
        });
      }
      
      // Format dates for datetime-local input (YYYY-MM-DDTHH:MM)
      const formatDateTimeLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      const startDateFormatted = formatDateTimeLocal(weekStartDate);
      const endDateFormatted = formatDateTimeLocal(weekEndDate);
      
      // Set the calculated dates in the form
      setValue('start_date', startDateFormatted);
      setValue('end_date', endDateFormatted);
      
      // Show a helpful toast message
      toast.success(`Week ${weekNumber} dates calculated: ${weekStartDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} - ${weekEndDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`);
      
    } catch (error) {
      console.error('Error calculating week dates:', error);
      toast.error('Failed to calculate week dates');
    }
  };

  // Helper function to adjust date to nearest Monday (for start date)
  const adjustToMonday = (date) => {
    const newDate = new Date(date);
    const dayOfWeek = newDate.getDay();
    
    if (dayOfWeek === 1) {
      // Already Monday
      return newDate;
    } else if (dayOfWeek === 0) {
      // Sunday, go forward 1 day to Monday
      newDate.setDate(newDate.getDate() + 1);
    } else {
      // Tuesday-Saturday, go back to previous Monday
      newDate.setDate(newDate.getDate() - (dayOfWeek - 1));
    }
    
    newDate.setHours(0, 0, 0, 0); // Set to 00:00
    return newDate;
  };

  // Helper function to adjust date to nearest Sunday (for end date)
  const adjustToSunday = (date) => {
    const newDate = new Date(date);
    const dayOfWeek = newDate.getDay();
    
    if (dayOfWeek === 0) {
      // Already Sunday
      newDate.setHours(22, 0, 0, 0); // Set to 22:00
      return newDate;
    } else {
      // Monday-Saturday, go forward to next Sunday
      newDate.setDate(newDate.getDate() + (7 - dayOfWeek));
      newDate.setHours(22, 0, 0, 0); // Set to 22:00
      return newDate;
    }
  };

  const handleEntityChange = (entity) => {
    setSelectedEntity(entity);
    setCurrentPage(1);
    setRecords([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    reset();
    if (entity !== 'topics' && entity !== 'sub_topics') {
      setCoursesForTopicsList([]);
    }
    setRefBookFilters({ subject: '' });
    setTopicListFilters({
      subject_id: '',
      school_year_id: '',
      status: '',
      parent_topic_id: '',
    });
    
    // Load academic years when switching to weeks entity
    if (entity === 'weeks') {
      fetchAcademicYears();
    }
  };

  const handleCreate = () => {
    if (!canCreateEntity(selectedEntity)) {
      toast.error('You do not have permission to create records for this entity.');
      return;
    }
    setEditMode(false);
    setViewMode(false);
    setCurrentRecord(null);
    reset();
    if (selectedEntity === 'sub_topics' && topicListFilters.parent_topic_id) {
      const chosenParent = dedupedSubTopicsTopicsList.find(
        (t) => String(t.master_topic_id) === String(topicListFilters.parent_topic_id)
      );
      if (chosenParent?.sample_course_topic_id) {
        setValue('course_topic_id', String(chosenParent.sample_course_topic_id), {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
    if (selectedEntity === 'roles') {
      setValue('permission_ids', []);
      setValue('parent_role', '');
    }
    setDialogOpen(true);
    if (selectedEntity === 'houses') {
      setValue('sort_order', 0, { shouldValidate: false });
      setValue('status', 1, { shouldValidate: false });
    }
    if (selectedEntity === 'mobile_subscription_plans') {
      setValue('currency', 'gbp', { shouldValidate: false });
      setValue('billing_interval', 'month', { shouldValidate: false });
      setValue('price_pence', 0, { shouldValidate: false });
      setValue('sort_order', 0, { shouldValidate: false });
      setValue('is_active', '1', { shouldValidate: false });
      setValue('is_public', '1', { shouldValidate: false });
    }
  };

  const applySchoolAdmissionCycleFields = (record) => {
    const cycle = record?.latest_admission_cycle || record?.latestAdmissionCycle;
    if (!cycle) {
      setValue('entry_year', '');
      setValue('open_day_date', '');
      setValue('open_day_notes', '');
      setValue('exam_date', '');
      setValue('exam_date_notes', '');
      setValue('registration_deadline', '');
      setValue('registration_notes', '');
      setValue('admission_notes', '');
      return;
    }
    setValue('entry_year', cycle.entry_year ?? '');
    setValue('open_day_date', cycle.open_day_date ? String(cycle.open_day_date).slice(0, 10) : '');
    setValue('open_day_notes', cycle.open_day_notes ?? '');
    setValue('exam_date', cycle.exam_date ? String(cycle.exam_date).slice(0, 10) : '');
    setValue('exam_date_notes', cycle.exam_date_notes ?? '');
    setValue(
      'registration_deadline',
      cycle.registration_deadline ? String(cycle.registration_deadline).slice(0, 10) : ''
    );
    setValue('registration_notes', cycle.registration_notes ?? '');
    setValue('admission_notes', cycle.notes ?? '');
  };

  const handleEdit = (record) => {
    if (!canUpdateEntity(selectedEntity)) {
      toast.error('You do not have permission to edit records for this entity.');
      return;
    }
    setEditMode(true);
    setViewMode(false);
    setCurrentRecord(record);
    setPdfFile(null);
    setEpubFile(null);

    // Dynamically populate form fields based on entity configuration
    const config = ENTITY_CONFIGS[selectedEntity];
    if (config && config.fields) {
      config.fields.forEach(fieldConfig => {
        if (fieldConfig.type === 'file') return; // file inputs can't be pre-populated

        let value = record[fieldConfig.name];

        // Special handling for Academic Years - parse start_end_year to populate start_year and end_year
        if (selectedEntity === 'academic_years' && (fieldConfig.name === 'start_year' || fieldConfig.name === 'end_year')) {
          const startEndYear = record.start_end_year;
          if (startEndYear && typeof startEndYear === 'string' && startEndYear.includes('/')) {
            const [startYear, endYear] = startEndYear.split('/');
            value = fieldConfig.name === 'start_year' ? parseInt(startYear) : parseInt(endYear);
          }
        }
        else if (selectedEntity === 'schools' && fieldConfig.name === 'exam_stages_text') {
          const stages = Array.isArray(record.exam_stages) ? record.exam_stages : [];
          value = stages.map(stageToEditLine).join('\n');
        }
        else if (selectedEntity === 'regions' && fieldConfig.name === 'selective') {
          value = value === true || value === 1 || value === '1' ? 1 : 0;
        }
        else if (selectedEntity === 'sub_topics' && fieldConfig.name === 'course_topic_id') {
          // Parent is the master topics parent_id; the dropdown is keyed by master topic id.
          value = record.parent_id ?? '';
        }
        else if (selectedEntity === 'sub_topics' && fieldConfig.name === 'areas') {
          value = Array.isArray(record.areas) ? record.areas.join('\n') : (record.areas || '');
        }
        else if (selectedEntity === 'nvr_rules' && fieldConfig.name === 'rule_configuration') {
          value = value && typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || '');
        }
        // Handle special cases for different field types
        else if (fieldConfig.type === 'datetime-local' && value) {
          // Convert datetime to datetime-local format (remove seconds if present)
          value = value.split(' ')[0] + 'T' + value.split(' ')[1]?.substring(0, 5) || value.split(' ')[0];
        } else if (fieldConfig.type === 'number' && value !== '' && value != null) {
          value = Number(value);
        }

        if (selectedEntity === 'roles' && fieldConfig.name === 'parent_role') {
          setValue(fieldConfig.name, String(value || '').toLowerCase());
        } else if (fieldConfig.type === 'number') {
          setValue(
            fieldConfig.name,
            value === '' || value === null || value === undefined ? '' : Number(value)
          );
        } else if (fieldConfig.type === 'checkbox') {
          setValue(fieldConfig.name, Boolean(value));
        } else if (fieldConfig.type === 'select') {
          if (selectedEntity === 'mobile_subscription_plans' && ['is_active', 'is_public'].includes(fieldConfig.name)) {
            setValue(
              fieldConfig.name,
              value === true || value === 1 || value === '1' || value === 'true' ? '1' : '0'
            );
          } else {
            setValue(
              fieldConfig.name,
              value === '' || value === null || value === undefined ? '' : String(value)
            );
          }
        } else {
          setValue(fieldConfig.name, value ?? '');
        }
      });
    }

    if (selectedEntity === 'schools') {
      if (record.reasoning_included === true || record.reasoning_included === 1) {
        setValue('reasoning_included', '1');
      } else if (record.reasoning_included === false || record.reasoning_included === 0) {
        setValue('reasoning_included', '0');
      } else {
        setValue('reasoning_included', '');
      }
      applySchoolAdmissionCycleFields(record);
    }
    
    if (selectedEntity === 'roles') {
      setViewRolePermissionsSnapshot(null);
      setValue('permission_ids', []);
      api.get(`admin/rbac/roles/${record.id}`)
        .then((res) => {
          const permissionIds = (res.data?.data?.permissions || []).map((p) => Number(p.id));
          setValue('permission_ids', permissionIds, { shouldValidate: false, shouldDirty: false });
        })
        .catch((error) => {
          console.error('Error loading role permissions:', error);
          setValue('permission_ids', []);
        });
    }

    setDialogOpen(true);
  };

  const handleView = (record) => {
    if (!canAccessEntityTab(selectedEntity)) {
      toast.error('You do not have permission to view records for this entity.');
      return;
    }
    setViewMode(true);
    setEditMode(false);
    setCurrentRecord(record);
    setPdfFile(null);
    setEpubFile(null);

    // Dynamically populate form fields based on entity configuration
    const config = ENTITY_CONFIGS[selectedEntity];
    if (config && config.fields) {
      config.fields.forEach(fieldConfig => {
        if (fieldConfig.type === 'file') return; // file inputs can't be pre-populated

        let value = record[fieldConfig.name];

        // Special handling for Academic Years - parse start_end_year to populate start_year and end_year
        if (selectedEntity === 'academic_years' && (fieldConfig.name === 'start_year' || fieldConfig.name === 'end_year')) {
          const startEndYear = record.start_end_year;
          if (startEndYear && typeof startEndYear === 'string' && startEndYear.includes('/')) {
            const [startYear, endYear] = startEndYear.split('/');
            value = fieldConfig.name === 'start_year' ? parseInt(startYear) : parseInt(endYear);
          }
        }
        else if (selectedEntity === 'schools' && fieldConfig.name === 'exam_stages_text') {
          const stages = Array.isArray(record.exam_stages) ? record.exam_stages : [];
          value = stages.map(stageToEditLine).join('\n');
        }
        else if (selectedEntity === 'regions' && fieldConfig.name === 'selective') {
          value = value === true || value === 1 || value === '1' ? 1 : 0;
        }
        else if (selectedEntity === 'sub_topics' && fieldConfig.name === 'course_topic_id') {
          // Parent is the master topics parent_id; the dropdown is keyed by master topic id.
          value = record.parent_id ?? '';
        }
        else if (selectedEntity === 'sub_topics' && fieldConfig.name === 'areas') {
          value = Array.isArray(record.areas) ? record.areas.join('\n') : (record.areas || '');
        }
        else if (selectedEntity === 'nvr_rules' && fieldConfig.name === 'rule_configuration') {
          value = value && typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || '');
        }
        // Handle special cases for different field types
        else if (fieldConfig.type === 'datetime-local' && value) {
          // Convert datetime to datetime-local format (remove seconds if present)
          value = value.split(' ')[0] + 'T' + value.split(' ')[1]?.substring(0, 5) || value.split(' ')[0];
        } else if (fieldConfig.type === 'number' && value !== '' && value != null) {
          value = Number(value);
        }

        if (selectedEntity === 'roles' && fieldConfig.name === 'parent_role') {
          setValue(fieldConfig.name, String(value || '').toLowerCase());
        } else if (fieldConfig.type === 'number') {
          setValue(
            fieldConfig.name,
            value === '' || value === null || value === undefined ? '' : Number(value)
          );
        } else if (fieldConfig.type === 'checkbox') {
          setValue(fieldConfig.name, Boolean(value));
        } else if (fieldConfig.type === 'select') {
          if (selectedEntity === 'mobile_subscription_plans' && ['is_active', 'is_public'].includes(fieldConfig.name)) {
            setValue(
              fieldConfig.name,
              value === true || value === 1 || value === '1' || value === 'true' ? '1' : '0'
            );
          } else {
            setValue(
              fieldConfig.name,
              value === '' || value === null || value === undefined ? '' : String(value)
            );
          }
        } else {
          setValue(fieldConfig.name, value ?? '');
        }
      });
    }

    if (selectedEntity === 'schools') {
      if (record.reasoning_included === true || record.reasoning_included === 1) {
        setValue('reasoning_included', '1');
      } else if (record.reasoning_included === false || record.reasoning_included === 0) {
        setValue('reasoning_included', '0');
      } else {
        setValue('reasoning_included', '');
      }
      applySchoolAdmissionCycleFields(record);
    }
    
    if (selectedEntity === 'roles') {
      setViewRolePermissionsSnapshot(null);
      setValue('permission_ids', []);
      api.get(`admin/rbac/roles/${record.id}`)
        .then((res) => {
          const perms = res.data?.data?.permissions || [];
          setViewRolePermissionsSnapshot(perms);
          const permissionIds = perms.map((p) => Number(p.id));
          setValue('permission_ids', permissionIds, { shouldValidate: false, shouldDirty: false });
        })
        .catch(() => {
          setViewRolePermissionsSnapshot([]);
          setValue('permission_ids', []);
        });
    }
    
    setDialogOpen(true);
  };

  const handleDelete = async (record) => {
    if (!canDeleteEntity(selectedEntity)) {
      toast.error('You do not have permission to delete records for this entity.');
      return;
    }
    const entityName = ENTITY_CONFIGS[selectedEntity]?.name.slice(0, -1) || 'record';
    const recordName = record.name || record.week_number || record.start_end_year || `${entityName} #${record.id}`;
    const isRoleEntity = selectedEntity === 'roles';
    
    showConfirmDialog(
      isRoleEntity ? 'Set Inactive' : 'Delete Confirmation',
      isRoleEntity
        ? `Are you sure you want to set "${recordName}" as inactive?`
        : `Are you sure you want to delete "${recordName}"?\n\nThis action cannot be undone.`,
      () => performDelete(record),
      {
        confirmText: isRoleEntity ? 'Set Inactive' : 'Delete',
        cancelText: 'Cancel',
        severity: isRoleEntity ? 'warning' : 'error'
      }
    );
  };

  const performDelete = async (record) => {
    const entityName = ENTITY_CONFIGS[selectedEntity]?.name.slice(0, -1) || 'record';
    
    try {
      setDeletingRecordId(record.id);
      const response = await api.delete(`admin/master-form/${selectedEntity}/${record.id}`);
      if (response.data.success) {
        toast.success(
          response.data.message || (selectedEntity === 'roles'
            ? `${entityName} set to inactive successfully`
            : `${entityName} deleted successfully`)
        );
        fetchRecords();
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Record not found');
      } else if (error.response?.status === 422) {
        toast.error(error.response.data?.message || 'Cannot delete: record is still referenced.');
      } else {
        toast.error(selectedEntity === 'roles' ? 'Failed to set inactive' : 'Failed to delete record');
      }
      console.error('Error deleting record:', error);
    } finally {
      setDeletingRecordId(null);
    }
  };

  const handleRestore = async (record) => {
    if (!canUpdateEntity(selectedEntity)) {
      toast.error('You do not have permission to restore records for this entity.');
      return;
    }
    const entityName = ENTITY_CONFIGS[selectedEntity]?.name.slice(0, -1) || 'record';
    const recordName = record.name || record.week_number || record.start_end_year || `${entityName} #${record.id}`;
    const isRoleEntity = selectedEntity === 'roles';
    
    showConfirmDialog(
      isRoleEntity ? 'Set Active' : 'Restore Confirmation',
      isRoleEntity
        ? `Are you sure you want to set "${recordName}" as active?`
        : `Are you sure you want to restore "${recordName}"?`,
      () => performRestore(record),
      {
        confirmText: isRoleEntity ? 'Set Active' : 'Restore',
        cancelText: 'Cancel',
        severity: 'info'
      }
    );
  };

  const performRestore = async (record) => {
    const entityName = ENTITY_CONFIGS[selectedEntity]?.name.slice(0, -1) || 'record';

    try {
      setRestoringRecordId(record.id);
      const response = await api.post(`admin/master-form/${selectedEntity}/${record.id}/restore`);
      if (response.data.success) {
        toast.success(
          response.data.message || (selectedEntity === 'roles'
            ? `${entityName} set to active successfully`
            : `${entityName} restored successfully`)
        );
        fetchRecords();
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Cannot restore this record');
      } else if (error.response?.status === 404) {
        toast.error('Record not found');
      } else {
        toast.error(selectedEntity === 'roles' ? 'Failed to set active' : 'Failed to restore record');
      }
      console.error('Error restoring record:', error);
    } finally {
      setRestoringRecordId(null);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      let response;
      
      // Format data based on entity type
      let formattedData = { ...data };
      if (selectedEntity === 'weeks') {
        // Validate week number
        const weekNumber = parseInt(data.week_number);
        if (isNaN(weekNumber) || weekNumber < 1 || weekNumber > 52) {
          toast.error('Week number must be between 1 and 52');
          setSubmitting(false);
          return;
        }
        
        // Format dates according to backend requirements
        // Convert datetime-local format to proper ISO format
        // Use local date parsing to avoid timezone issues
        const [startDateStr, startTimeStr] = data.start_date.split('T');
        const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
        let startDate = new Date(startYear, startMonth - 1, startDay, ...startTimeStr.split(':').map(Number));
        
        const [endDateStr, endTimeStr] = data.end_date.split('T');
        const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number);
        let endDate = new Date(endYear, endMonth - 1, endDay, ...endTimeStr.split(':').map(Number));
        
        // Debug logging for date validation
        if (process.env.NODE_ENV === 'development') {
          console.log('Date validation debug:', {
            startDateInput: data.start_date,
            endDateInput: data.end_date,
            startDate: startDate.toString(),
            endDate: endDate.toString(),
            startDayOfWeek: startDate.getDay(),
            endDayOfWeek: endDate.getDay(),
            endHours: endDate.getHours()
          });
        }
        
        // Auto-correct dates if they're not Monday/Sunday
        let correctedStartDate = startDate;
        let correctedEndDate = endDate;
        let datesCorrected = false;
        
        if (startDate.getDay() !== 1) {
          correctedStartDate = adjustToMonday(startDate);
          datesCorrected = true;
        }
        
        if (endDate.getDay() !== 0) {
          correctedEndDate = adjustToSunday(endDate);
          datesCorrected = true;
        }
        
        // If dates were corrected, show a warning but continue
        if (datesCorrected) {
          toast.warning('Dates have been auto-corrected to follow Monday-Sunday pattern');
          console.log('Dates corrected:', {
            originalStart: startDate.toString(),
            correctedStart: correctedStartDate.toString(),
            originalEnd: endDate.toString(),
            correctedEnd: correctedEndDate.toString()
          });
        }
        
        // Use corrected dates for backend submission
        startDate = correctedStartDate;
        endDate = correctedEndDate;
        
        // Format data for backend
        formattedData.academic_year_id = parseInt(data.academic_year_id);
        formattedData.week_number = String(weekNumber); // Backend expects string, not integer
        formattedData.start_date = startDate.toISOString().slice(0, 19); // Remove milliseconds
        formattedData.end_date = endDate.toISOString().slice(0, 19); // Remove milliseconds
        formattedData.status = parseInt(data.status) || 1; // Default to Pending if not set
      }
      
      // Academic Years validation and formatting (Enhanced to match backend validation)
      if (selectedEntity === 'academic_years') {
        const startYear = parseInt(data.start_year);
        const endYear = parseInt(data.end_year);
        
        // Validate data types
        if (isNaN(startYear) || isNaN(endYear)) {
          toast.error('Both years must be valid numbers');
          setSubmitting(false);
          return;
        }
        
        // Validate year range (1900-2100)
        if (startYear < 1900 || startYear > 2100 || endYear < 1900 || endYear > 2100) {
          toast.error('Years must be between 1900 and 2100');
          setSubmitting(false);
          return;
        }
        
        // Validate consecutive years (end_year must equal start_year + 1)
        if (endYear !== startYear + 1) {
          toast.error('Must be consecutive years (e.g., 2025/2026, not 2025/2028)');
          setSubmitting(false);
          return;
        }
        
        // Format data to match backend expectations
        formattedData.start_year = startYear;
        formattedData.end_year = endYear;
        formattedData.status = parseInt(data.status) || 1; // Default to Pending if not set
      }

      if (selectedEntity === 'categories') {
        formattedData.parent_id = data.parent_id === '' || data.parent_id == null ? null : parseInt(data.parent_id);
        formattedData.status = parseInt(data.status) || 1;
      }

      if (selectedEntity === 'exam_boards') {
        formattedData.status = parseInt(data.status, 10) || 2;
        formattedData.code =
          data.code === '' || data.code == null
            ? null
            : String(data.code).trim().toUpperCase();
      }

      if (selectedEntity === 'mobile_subscription_plans') {
        formattedData.price_pence = parseInt(data.price_pence, 10);
        if (Number.isNaN(formattedData.price_pence) || formattedData.price_pence < 0) {
          toast.error('Price (pence) must be a valid number (0 or more)');
          setSubmitting(false);
          return;
        }
        formattedData.sort_order =
          data.sort_order === '' || data.sort_order == null
            ? 0
            : parseInt(data.sort_order, 10) || 0;
        formattedData.currency =
          data.currency === '' || data.currency == null
            ? 'gbp'
            : String(data.currency).trim().toLowerCase();
        formattedData.is_active =
          data.is_active === true ||
          data.is_active === 1 ||
          data.is_active === '1' ||
          data.is_active === 'true';
        // A row saved before the column existed has no value; treat that as visible.
        formattedData.is_public =
          data.is_public === undefined ||
          data.is_public === null ||
          data.is_public === '' ||
          data.is_public === true ||
          data.is_public === 1 ||
          data.is_public === '1' ||
          data.is_public === 'true';
        formattedData.slug =
          data.slug === '' || data.slug == null ? null : String(data.slug).trim().toLowerCase();
        ['stripe_price_id', 'apple_product_id', 'google_product_id', 'description'].forEach((key) => {
          if (formattedData[key] === '') formattedData[key] = null;
        });
      }

      if (selectedEntity === 'schools') {
        formattedData.target_school_id = parseInt(data.target_school_id, 10);
        formattedData.exam_board_id =
          data.exam_board_id === '' || data.exam_board_id == null
            ? null
            : parseInt(data.exam_board_id, 10);
        formattedData.region_id =
          data.region_id === '' || data.region_id == null
            ? null
            : parseInt(data.region_id, 10);
        formattedData.consortium_id =
          data.consortium_id === '' || data.consortium_id == null
            ? null
            : parseInt(data.consortium_id, 10);
        formattedData.exam_format = data.exam_format || null;
        formattedData.exam_stages = String(data.exam_stages_text || '')
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [name, subject, questionCount, duration, durationTo] = line.split('|').map((part) => part.trim());
            return {
              name: name || null,
              subject: subject || null,
              question_count: questionCount ? parseInt(questionCount, 10) : null,
              duration_minutes: duration ? parseInt(duration, 10) : null,
              duration_to_minutes: durationTo ? parseInt(durationTo, 10) : null,
            };
          });
        delete formattedData.exam_stages_text;
        formattedData.latitude =
          data.latitude === '' || data.latitude == null
            ? null
            : Number(data.latitude);
        formattedData.longitude =
          data.longitude === '' || data.longitude == null
            ? null
            : Number(data.longitude);
        formattedData.gender =
          data.gender === '' || data.gender == null ? null : data.gender;
        formattedData.selection_type =
          data.selection_type === '' || data.selection_type == null ? null : data.selection_type;
        delete formattedData.consortium_name;
        formattedData.exam_provider =
          data.exam_provider === '' || data.exam_provider == null ? null : data.exam_provider;
        formattedData.question_style =
          data.question_style === '' || data.question_style == null ? null : data.question_style;
        if (data.reasoning_included === '' || data.reasoning_included == null) {
          formattedData.reasoning_included = null;
        } else {
          formattedData.reasoning_included =
            data.reasoning_included === true ||
            data.reasoning_included === 1 ||
            data.reasoning_included === '1';
        }
        formattedData.subjects_tested =
          data.subjects_tested === '' || data.subjects_tested == null ? null : data.subjects_tested;
        formattedData.entry_format =
          data.entry_format === '' || data.entry_format == null ? null : data.entry_format;
        formattedData.pass_criteria =
          data.pass_criteria === '' || data.pass_criteria == null ? null : data.pass_criteria;
        formattedData.entry_year =
          data.entry_year === '' || data.entry_year == null
            ? null
            : parseInt(data.entry_year, 10);
        ['open_day_date', 'exam_date', 'registration_deadline'].forEach((key) => {
          formattedData[key] =
            data[key] === '' || data[key] == null ? null : data[key];
        });
        ['open_day_notes', 'exam_date_notes', 'registration_notes', 'admission_notes'].forEach((key) => {
          formattedData[key] =
            data[key] === '' || data[key] == null ? null : data[key];
        });
        ['address', 'phone', 'email', 'logo', 'website', 'question_style', 'subjects_tested'].forEach((key) => {
          formattedData[key] =
            data[key] === '' || data[key] == null ? null : data[key];
        });
      }

      if (selectedEntity === 'houses') {
        const so = parseInt(data.sort_order, 10);
        formattedData.sort_order = Number.isNaN(so) ? 0 : so;
        formattedData.status = parseInt(data.status, 10) || 1;
        formattedData.key =
          data.key === '' || data.key == null ? null : String(data.key).trim() || null;
      }

      if (selectedEntity === 'topics') {
        formattedData.subject_id = parseInt(data.subject_id, 10);
        formattedData.school_year_id = parseInt(data.school_year_id, 10);
        formattedData.course_id =
          data.course_id === '' || data.course_id == null ? null : parseInt(data.course_id, 10);
      }

      if (selectedEntity === 'sub_topics') {
        formattedData.subject_id = parseInt(data.subject_id, 10);
        formattedData.school_year_id = parseInt(data.school_year_id, 10);
        formattedData.course_id =
          data.course_id === '' || data.course_id == null ? null : parseInt(data.course_id, 10);
        formattedData.course_topic_id =
          data.course_topic_id === '' || data.course_topic_id == null
            ? null
            : parseInt(data.course_topic_id, 10);
        if (typeof data.areas === 'string') {
          const parsedAreas = data.areas
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
          formattedData.areas = parsedAreas.length ? parsedAreas : null;
        } else if (Array.isArray(data.areas)) {
          formattedData.areas = data.areas.map((s) => String(s).trim()).filter(Boolean);
          if (!formattedData.areas.length) formattedData.areas = null;
        } else {
          formattedData.areas = null;
        }
        const sq = typeof data.sample_questions === 'string' ? data.sample_questions.trim() : '';
        formattedData.sample_questions = sq || null;
      }

      if (selectedEntity === 'nvr_rules') {
        formattedData.topic_id =
          data.topic_id === '' || data.topic_id == null ? null : parseInt(data.topic_id, 10);
        formattedData.sub_topic_id =
          data.sub_topic_id === '' || data.sub_topic_id == null ? null : parseInt(data.sub_topic_id, 10);
        const so = parseInt(data.sort_order, 10);
        formattedData.sort_order = Number.isNaN(so) ? 0 : so;
        formattedData.status = data.status === '' || data.status == null ? 1 : parseInt(data.status, 10);
        try {
          formattedData.rule_configuration = JSON.parse(data.rule_configuration);
        } catch {
          toast.error('Rule configuration must be valid JSON');
          setSubmitting(false);
          return;
        }
      }

      if (selectedEntity === 'roles') {
        formattedData.permission_ids = Array.isArray(data.permission_ids)
          ? data.permission_ids.map((id) => parseInt(id)).filter((id) => !isNaN(id))
          : [];
        const parentRoleValue = String(data.parent_role || '').toLowerCase();
        formattedData.parent_role = parentRoleValue === '' ? null : parentRoleValue;
        if (editMode) {
          delete formattedData.name;
        }
      }

      if (selectedEntity === 'reference_books') {
        const fd = new FormData();
        fd.append('title', data.title || '');
        if (data.author) fd.append('author', data.author);
        if (data.genre) fd.append('genre', data.genre);
        if (data.subject) fd.append('subject', data.subject);
        if (data.published_date) fd.append('published_date', data.published_date);
        fd.append('is_primary', data.is_primary ? '1' : '0');
        fd.append('status', data.status !== undefined && data.status !== '' ? String(data.status) : '1');
        if (pdfFile) fd.append('pdf_file', pdfFile);
        if (epubFile) fd.append('epub_file', epubFile);
        if (editMode && currentRecord) {
          fd.append('_method', 'PUT');
          response = await api.post(`admin/master-form/reference_books/${currentRecord.id}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          response = await api.post('admin/master-form/reference_books', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      } else {

      // Debug logging for API request
      if (process.env.NODE_ENV === 'development') {
        console.log('API Request Debug:', {
          method: editMode ? 'PUT' : 'POST',
          endpoint: editMode ? `admin/master-form/${selectedEntity}/${currentRecord?.id}` : `admin/master-form/${selectedEntity}`,
          payload: formattedData,
          entity: selectedEntity
        });
      }

      if (editMode && currentRecord) {
        if (selectedEntity === 'roles') {
          response = await api.put(`admin/rbac/roles/${currentRecord.id}`, formattedData);
        } else {
          response = await api.put(`admin/master-form/${selectedEntity}/${currentRecord.id}`, formattedData);
        }
      } else {
        if (selectedEntity === 'roles') {
          response = await api.post('admin/rbac/roles', formattedData);
        } else {
          response = await api.post(`admin/master-form/${selectedEntity}`, formattedData);
        }
      }
      }

      if (response.data.success) {
        const entityName = ENTITY_CONFIGS[selectedEntity]?.name.slice(0, -1) || 'Record';
        toast.success(response.data.message || `${entityName} ${editMode ? 'updated' : 'created'} successfully`);
        setDialogOpen(false);
        setPdfFile(null);
    setEpubFile(null);
        reset();
        fetchRecords();
        if (selectedEntity === 'schools') {
          fetchSchoolRegionSummary();
        }
      }
    } catch (error) {
      // Enhanced error logging
      console.error('API Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          method: error.config?.method,
          url: error.config?.url,
          data: error.config?.data
        }
      });

      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        console.log('Validation errors:', errors);
        if (errors && typeof errors === 'object') {
          Object.keys(errors).forEach(key => {
            const value = Array.isArray(errors[key]) ? errors[key].join(', ') : String(errors[key]);
            toast.error(`${key}: ${value}`);
          });
        } else {
          toast.error(error.response?.data?.message || 'Validation error occurred');
        }
      } else if (error.response?.status === 404) {
        toast.error('Record not found');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (error.response?.status === 500) {
        toast.error(`Server error: ${error.response?.data?.message || 'Internal server error'}`);
      } else if (error.response) {
        // API responded with error status
        toast.error(`API Error (${error.response.status}): ${error.response?.data?.message || error.response.statusText}`);
      } else if (error.request) {
        // Network error
        toast.error('Network error: Unable to reach server');
      } else {
        // Other error
        toast.error(`Failed to ${editMode ? 'update' : 'create'} record: ${error.message}`);
      }
      console.error('Error saving record:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Test function to manually test weeks API
  const testWeeksAPI = async () => {
    try {
      const testData = {
        academic_year_id: 8,
        week_number: "1",
        start_date: "2027-06-07T00:00:00",
        end_date: "2027-06-13T22:00:00",
        status: 1
      };
      
      console.log('Testing weeks API with data:', testData);
      const response = await api.post('admin/master-form/weeks', testData);
      console.log('Test API Response:', response.data);
      toast.success('Test API call successful!');
    } catch (error) {
      console.error('Test API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      toast.error(`Test API failed: ${error.response?.data?.message || error.message}`);
    }
  };

  // Add this to window for manual testing
  if (process.env.NODE_ENV === 'development') {
    window.testWeeksAPI = testWeeksAPI;
  }

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentRecord(null);
    setEditMode(false);
    setViewMode(false);
    setViewRolePermissionsSnapshot(null);
    setPdfFile(null);
    setEpubFile(null);
    reset();
  };

  const renderFormFields = () => {
    if (!selectedEntity) return null;

    const config = ENTITY_CONFIGS[selectedEntity];
    if (!config || !config.fields) return null;

    return (
      <Grid container spacing={2}>
        {config.fields.map((fieldConfig) => {
          const gridSize = config.fields.length > 2 ? 12 : 6;
          
          return (
            <Grid item xs={12} sm={fieldConfig.type === 'textarea' ? 12 : gridSize} key={fieldConfig.name}>
              <Controller
                name={fieldConfig.name}
                control={control}
                defaultValue=""
                render={({ field }) => {
                  const fieldError = errors[fieldConfig.name];
                  
                  // Handle different field types
                  switch (fieldConfig.type) {
                    case 'select':
                      // Special handling for academic_year_id in weeks
                      if (fieldConfig.name === 'academic_year_id') {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label} {fieldConfig.required && '*'}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || academicYearsLoading}
                              onChange={(e) => {
                                field.onChange(e);
                                // Auto-suggest week dates when academic year changes for weeks
                                if (selectedEntity === 'weeks') {
                                  const academicYearId = e.target.value;
                                  const weekNumber = parseInt(control._formValues.week_number);
                                  if (academicYearId && !isNaN(weekNumber) && weekNumber >= 1 && weekNumber <= 52) {
                                    calculateWeekDates(academicYearId, weekNumber);
                                  }
                                }
                              }}
                            >
                              {academicYearsLoading ? (
                                <MenuItem disabled>
                                  <Typography variant="body2" color="text.secondary">
                                    Loading academic years...
                                  </Typography>
                                </MenuItem>
                              ) : academicYears.length === 0 ? (
                                <MenuItem disabled>
                                  <Typography variant="body2" color="text.secondary">
                                    No academic years available
                                  </Typography>
                                </MenuItem>
                              ) : (
                                academicYears.map((year) => (
                                  <MenuItem key={year.id} value={year.id}>
                                    {year.start_end_year || `${year.start_year}/${year.end_year}`}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }
                      
                      // Parent category select for categories entity
                      if (selectedEntity === 'categories' && fieldConfig.name === 'parent_id') {
                        const parentOptions = editMode && currentRecord
                          ? categoriesList.filter((cat) => cat.id !== currentRecord.id)
                          : categoriesList;
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label} {fieldConfig.required && '*'}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || categoriesLoading}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                            >
                              <MenuItem value="">None (root category)</MenuItem>
                              {parentOptions.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                  {cat.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (selectedEntity === 'roles' && fieldConfig.name === 'parent_role') {
                        const disabled = viewMode || isParentRoleName;
                        const baseRoleRequired = !isParentRoleName && !viewMode;
                        const baseRoleLabelText = baseRoleRequired
                          ? `${fieldConfig.label} *`
                          : fieldConfig.label;
                        const baseRoleLabelId = 'master-form-role-base-role';
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel id={baseRoleLabelId} shrink>
                              {baseRoleLabelText}
                            </InputLabel>
                            <Select
                              labelId={baseRoleLabelId}
                              label={baseRoleLabelText}
                              disabled={disabled}
                              name={field.name}
                              inputRef={field.ref}
                              onBlur={field.onBlur}
                              displayEmpty
                              value={field.value ?? ''}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return (
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      {isParentRoleName ? 'No base role' : 'Select base role'}
                                    </Typography>
                                  );
                                }
                                const opt = fieldConfig.options?.find((o) => o.value === selected);
                                return opt?.label ?? selected;
                              }}
                              onChange={(e) => {
                                const selected = String(e.target.value || '').toLowerCase();
                                field.onChange(selected);
                                if (!viewMode && selected) {
                                  applyParentRolePermissions(selected);
                                }
                              }}
                            >
                              {fieldConfig.options?.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (selectedEntity === 'schools' && fieldConfig.name === 'exam_board_id') {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || examBoardsLoading}
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                            >
                              <MenuItem value="">None</MenuItem>
                              {examBoardsList.map((eb) => (
                                <MenuItem key={eb.id} value={String(eb.id)}>
                                  {(eb.code ? `${eb.code} — ` : '') + (eb.name || '')}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      // Schools: school category from `target_schools` table
                      if (selectedEntity === 'schools' && fieldConfig.name === 'target_school_id') {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label} {fieldConfig.required && '*'}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || schoolCategoriesLoading}
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                            >
                              <MenuItem value="">Select school category</MenuItem>
                              {schoolCategoriesList.map((ts) => (
                                <MenuItem key={ts.id} value={String(ts.id)}>
                                  {ts.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (selectedEntity === 'schools' && fieldConfig.name === 'consortium_id') {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || consortiumsLoading}
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                            >
                              <MenuItem value="">No consortium</MenuItem>
                              {consortiumsList.map((item) => (
                                <MenuItem key={item.id} value={String(item.id)}>
                                  {item.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (selectedEntity === 'schools' && fieldConfig.name === 'region_id') {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || regionsLoading}
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                            >
                              <MenuItem value="">No region</MenuItem>
                              {regionsList.map((region) => (
                                <MenuItem key={region.id} value={String(region.id)}>
                                  {region.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      // NVR rules: map a rule onto a master topic / sub-topic
                      if (
                        selectedEntity === 'nvr_rules' &&
                        (fieldConfig.name === 'topic_id' || fieldConfig.name === 'sub_topic_id')
                      ) {
                        const isSubTopic = fieldConfig.name === 'sub_topic_id';
                        const parentTopicId = watch('topic_id');
                        const list = isSubTopic
                          ? nvrSubTopicsList.filter(
                              (t) => !parentTopicId || String(t.parent_id) === String(parentTopicId)
                            )
                          : nvrTopicsList;
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || nvrTopicsLoading}
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                if (!isSubTopic) {
                                  setValue('sub_topic_id', '');
                                }
                              }}
                            >
                              <MenuItem value="">{isSubTopic ? 'Any sub-topic' : 'Any topic'}</MenuItem>
                              {list.map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>
                                  {t.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                {fieldError.message}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      // Topics / Sub topics: subject_id
                      if (
                        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
                        fieldConfig.name === 'subject_id'
                      ) {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label} {fieldConfig.required && '*'}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || topicsDropdownsLoading}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                if (selectedEntity === 'sub_topics') {
                                  setValue('course_topic_id', '');
                                }
                              }}
                            >
                              <MenuItem value="">Select subject</MenuItem>
                              {topicsSubjectsList.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>{fieldError.message}</Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (
                        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
                        fieldConfig.name === 'school_year_id'
                      ) {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label} {fieldConfig.required && '*'}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={viewMode || schoolYearsForTopicsLoading}
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                if (!viewMode) {
                                  setValue('course_id', '');
                                }
                              }}
                            >
                              <MenuItem value="">Select school year</MenuItem>
                              {schoolYearsForTopicsList.map((sy) => (
                                <MenuItem key={sy.id} value={String(sy.id)}>
                                  {sy.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>{fieldError.message}</Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (
                        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
                        fieldConfig.name === 'course_id'
                      ) {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={
                                viewMode ||
                                coursesForTopicsLoading ||
                                !watchedSchoolYearId ||
                                watchedSchoolYearId === ''
                              }
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                            >
                              <MenuItem value="">None</MenuItem>
                              {coursesForTopicsList.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>{fieldError.message}</Typography>
                            )}
                          </FormControl>
                        );
                      }
                      // Sub Topics: course_topic_id (parent topic, optional; filtered by subject)
                      if (selectedEntity === 'sub_topics' && fieldConfig.name === 'course_topic_id') {
                        return (
                          <FormControl fullWidth error={!!fieldError}>
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              {...field}
                              label={fieldConfig.label}
                              disabled={
                                viewMode ||
                                subTopicsTopicsLoading ||
                                !watchedSubjectId ||
                                watchedSubjectId === ''
                              }
                              value={field.value != null && field.value !== '' ? String(field.value) : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                            >
                              <MenuItem value="">None</MenuItem>
                              {dedupedSubTopicsTopicsList.map((t) => (
                                <MenuItem
                                  key={t.master_topic_id}
                                  value={String(t.sample_course_topic_id)}
                                >
                                  {t.name}
                                  {t.subject?.name ? ` (${t.subject.name})` : ''}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldError && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>{fieldError.message}</Typography>
                            )}
                          </FormControl>
                        );
                      }
                      
                      // Regular select field
                      return (
                        <FormControl fullWidth error={!!fieldError}>
                          <InputLabel>{fieldConfig.label} {fieldConfig.required && '*'}</InputLabel>
                          <Select
                            {...field}
                            label={fieldConfig.label}
                            disabled={viewMode}
                            displayEmpty={!fieldConfig.required}
                            value={field.value ?? ''}
                          >
                            {!fieldConfig.required && (
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                            )}
                            {fieldConfig.options?.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldError && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                              {fieldError.message}
                            </Typography>
                          )}
                        </FormControl>
                      );
                      
                    case 'textarea':
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={4}
                          label={`${fieldConfig.label} ${fieldConfig.required ? '*' : ''}`}
                          error={!!fieldError}
                          helperText={fieldError?.message || fieldConfig.helperText}
                          disabled={viewMode}
                          inputProps={{ maxLength: fieldConfig.maxLength }}
                        />
                      );
                      
                    case 'datetime-local':
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          label={`${fieldConfig.label} ${fieldConfig.required ? '*' : ''}`}
                          type="datetime-local"
                          error={!!fieldError}
                          helperText={fieldError?.message || fieldConfig.helperText}
                          disabled={viewMode}
                          InputLabelProps={{ shrink: true }}
                        />
                      );
                      
                    case 'date':
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          label={`${fieldConfig.label} ${fieldConfig.required ? '*' : ''}`}
                          type="date"
                          error={!!fieldError}
                          helperText={fieldError?.message || fieldConfig.helperText}
                          disabled={viewMode}
                          InputLabelProps={{ shrink: true }}
                        />
                      );

                    case 'checkbox':
                      return (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(field.value)}
                              onChange={(e) => field.onChange(e.target.checked)}
                              disabled={viewMode}
                            />
                          }
                          label={fieldConfig.label}
                        />
                      );

                    case 'file': {
                      const isPdf = fieldConfig.name === 'pdf_file';
                      const isEpub = fieldConfig.name === 'epub_file';
                      const selectedFile = isPdf ? pdfFile : isEpub ? epubFile : null;
                      const existingUrl = isPdf ? currentRecord?.pdf_url : isEpub ? currentRecord?.epub_url : null;
                      const setSelectedFile = isPdf ? setPdfFile : isEpub ? setEpubFile : () => {};
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            {fieldConfig.label}
                          </Typography>
                          {!viewMode && (
                            <input
                              type="file"
                              accept={fieldConfig.accept}
                              disabled={viewMode}
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                setSelectedFile(file);
                              }}
                              style={{ display: 'block', marginTop: 4 }}
                            />
                          )}
                          {editMode && existingUrl && !selectedFile && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ mt: 0.5, textTransform: 'none', p: 0 }}
                              onClick={() => setBookViewer({
                                title: currentRecord?.title,
                                pdfUrl: isPdf ? existingUrl : currentRecord?.pdf_url,
                                epubUrl: isEpub ? existingUrl : currentRecord?.epub_url,
                                bookId: currentRecord?.id,
                              })}
                            >
                              View existing {isPdf ? 'PDF' : 'EPUB'}
                            </Button>
                          )}
                          {viewMode && existingUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                              onClick={() => setBookViewer({
                                title: currentRecord?.title,
                                pdfUrl: isPdf ? existingUrl : currentRecord?.pdf_url,
                                epubUrl: isEpub ? existingUrl : currentRecord?.epub_url,
                                bookId: currentRecord?.id,
                              })}
                            >
                              View {isPdf ? 'PDF' : 'EPUB'}
                            </Button>
                          )}
                          {selectedFile && (
                            <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                              Selected: {selectedFile.name}
                            </Typography>
                          )}
                        </Box>
                      );
                    }

                    case 'number':
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          label={`${fieldConfig.label} ${fieldConfig.required ? '*' : ''}`}
                          type="number"
                          error={!!fieldError}
                          helperText={fieldError?.message || fieldConfig.helperText}
                          disabled={viewMode}
                          inputProps={{
                            min: fieldConfig.min,
                            max: fieldConfig.max,
                            maxLength: fieldConfig.maxLength
                          }}
                          onChange={(e) => {
                            field.onChange(e);
                            // Auto-fill end year when start year changes for academic_years
                            if (selectedEntity === 'academic_years' && fieldConfig.name === 'start_year') {
                              const startYear = parseInt(e.target.value);
                              if (!isNaN(startYear)) {
                                setValue('end_year', startYear + 1);
                              }
                            }
                          }}
                        />
                      );

                    default: // text, email, url, tel
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          label={`${fieldConfig.label} ${fieldConfig.required ? '*' : ''}`}
                          type={fieldConfig.type}
                          placeholder={fieldConfig.placeholder}
                          error={!!fieldError}
                          helperText={fieldError?.message || fieldConfig.helperText}
                          disabled={viewMode || (selectedEntity === 'roles' && editMode && fieldConfig.name === 'name')}
                          inputProps={{ maxLength: fieldConfig.maxLength }}
                          onChange={(e) => {
                            field.onChange(e);
                            // Auto-suggest week dates when week number changes for weeks
                            if (selectedEntity === 'weeks' && fieldConfig.name === 'week_number') {
                              const weekNumber = parseInt(e.target.value);
                              const academicYearId = control._formValues.academic_year_id;
                              if (!isNaN(weekNumber) && academicYearId && weekNumber >= 1 && weekNumber <= 52) {
                                calculateWeekDates(academicYearId, weekNumber);
                              }
                            }
                          }}
                        />
                      );
                  }
                }}
              />
            </Grid>
          );
        })}
        {selectedEntity === 'roles' && viewMode && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Assigned Permissions
              </Typography>
              {viewRolePermissionsSnapshot === null ? (
                <Typography variant="body2" color="text.secondary">
                  Loading assigned permissions…
                </Typography>
              ) : viewRolePermissionsSnapshot.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {groupPermissionsByGroup(viewRolePermissionsSnapshot).map(([groupName, perms], groupIdx, groups) => (
                    <Box key={groupName}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        {groupName}
                      </Typography>
                      <Grid container spacing={1}>
                        {perms.map((permission) => (
                          <Grid item xs={12} sm={4} key={permission.id}>
                            <Typography variant="body2" sx={{ py: 0.25 }}>
                              {formatPermissionDisplayName(permission.name)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                      {groupIdx < groups.length - 1 ? (
                        <Divider sx={{ my: 2 }} />
                      ) : null}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No permissions assigned.
                </Typography>
              )}
            </Box>
          </Grid>
        )}
        {selectedEntity === 'roles' && !viewMode && (
          <Grid item xs={12}>
            <Controller
              name="permission_ids"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                    Permissions
                  </Typography>
                  {permissionsLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading permissions...
                    </Typography>
                  ) : permissionsLoadError ? (
                    <Typography variant="body2" color="error">
                      {permissionsLoadError}
                    </Typography>
                  ) : permissionsList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No active permissions in the database. Run the permissions seeder or ensure rows have
                      is_active = true.
                    </Typography>
                  ) : roleScopedPermissions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No permissions match this parent role (portal) scope.
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
                      {groupPermissionsByGroup(roleScopedPermissions).map(([groupName, perms], groupIdx, groups) => (
                        <Box key={groupName}>
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ display: 'block', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}
                          >
                            {groupName}
                          </Typography>
                          <Grid container spacing={1}>
                            {perms.map((permission) => {
                              const selectedIds = (Array.isArray(field.value) ? field.value : []).map((id) =>
                                Number(id)
                              );
                              const pid = Number(permission.id);
                              const checked = selectedIds.includes(pid);
                              return (
                                <Grid item xs={12} sm={4} key={permission.id}>
                                  <FormControlLabel
                                    sx={{
                                      m: 0,
                                      mr: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      '& .MuiCheckbox-root': {
                                        padding: '6px',
                                        alignSelf: 'center',
                                      },
                                      '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem',
                                        lineHeight: 1.43,
                                        paddingTop: 0,
                                      },
                                    }}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            field.onChange([...new Set([...selectedIds, pid])]);
                                          } else {
                                            field.onChange(selectedIds.filter((id) => id !== pid));
                                          }
                                        }}
                                      />
                                    }
                                    label={formatPermissionDisplayName(permission.name)}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                          {groupIdx < groups.length - 1 ? (
                            <Divider sx={{ my: 2 }} />
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const SortableMasterRow = ({ rowId, disabled, sx, renderCells }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: String(rowId), disabled });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <TableRow
        ref={setNodeRef}
        style={style}
        sx={{
          ...(sx || {}),
          ...(isDragging ? { backgroundColor: 'rgba(102, 126, 234, 0.08)' } : {}),
        }}
      >
        {renderCells({ attributes, listeners, isDragging })}
      </TableRow>
    );
  };

  const renderTableHeaders = () => {
    if (!selectedEntity) return null;

    const config = ENTITY_CONFIGS[selectedEntity];
    if (!config) return null;

    // Check if entity already has its own active/status field (skip deleted_at Status column)
    const hasStatusField =
      config.fields.some((field) => field.name === 'status' || field.name === 'is_active');
    
    // Debug logging (can be removed in production)
    if (process.env.NODE_ENV === 'development') {
      console.log(`Entity: ${selectedEntity}, Has Status Field: ${hasStatusField}`);
      console.log('Fields:', config.fields.map(f => f.name));
    }

    return [
      (isTopicsReorderMode || isSubTopicsReorderMode) && (
        <TableCell key="drag" sx={{ width: 44 }} />
      ),
      <TableCell key="id">ID</TableCell>,
      ...getTableFields(selectedEntity).map((field) => (
        <TableCell key={field.name}>
          {field.label}
        </TableCell>
      )),
      selectedEntity !== 'academic_years' && <TableCell key="created_at">Created At</TableCell>,
      (!hasStatusField && selectedEntity !== 'schools') && <TableCell key="status">Status</TableCell>,
      <TableCell key="actions" align="center">Actions</TableCell>
    ].filter(Boolean);
  };

  const renderTableRow = (record) => {
    if (!selectedEntity) return null;

    const config = ENTITY_CONFIGS[selectedEntity];
    if (!config) return null;

    const isDeleted = Boolean(record.deleted_at);
    const isRoleEntity = selectedEntity === 'roles';

    // Helper function to render field value with fancy styling
    const renderFieldValue = (fieldConfig, value, currentRecord = null) => {
      if (selectedEntity === 'mobile_subscription_plans' && fieldConfig.name === 'is_active') {
        const active = value === true || value === 1 || value === '1' || value === 'true';
        return (
          <Chip
            label={active ? 'Active' : 'Inactive'}
            size="small"
            color={active ? 'success' : 'error'}
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
      }
      if (selectedEntity === 'mobile_subscription_plans' && fieldConfig.name === 'is_public') {
        const hidden = value === false || value === 0 || value === '0' || value === 'false';
        return (
          <Chip
            label={hidden ? 'Hidden' : 'Everyone'}
            size="small"
            color={hidden ? 'warning' : 'default'}
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
      }
      if (selectedEntity === 'mobile_subscription_plans' && fieldConfig.name === 'price_pence') {
        const pence = Number(value || 0);
        const currency = String(currentRecord?.currency || 'gbp').toLowerCase();
        const symbol = currency === 'gbp' ? '£' : `${currency.toUpperCase()} `;
        return (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {symbol}{(pence / 100).toFixed(2)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              ({pence}p)
            </Typography>
          </Typography>
        );
      }
      // Parent category name for categories entity
      if (selectedEntity === 'categories' && fieldConfig.name === 'parent_id') {
        const parentName = currentRecord?.parent?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {parentName || '—'}
          </Typography>
        );
      }
      if (
        selectedEntity === 'nvr_rules' &&
        (fieldConfig.name === 'topic_id' || fieldConfig.name === 'sub_topic_id')
      ) {
        const related =
          fieldConfig.name === 'topic_id'
            ? currentRecord?.topic
            : currentRecord?.sub_topic || currentRecord?.subTopic;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {related?.name || '—'}
          </Typography>
        );
      }
      if (selectedEntity === 'schools' && fieldConfig.name === 'gender') {
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {SCHOOL_GENDER_LABELS[value] || '—'}
          </Typography>
        );
      }
      // Schools: category name (target_schools)
      if (selectedEntity === 'schools' && fieldConfig.name === 'target_school_id') {
        const catName = currentRecord?.target_school?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {catName || '—'}
          </Typography>
        );
      }
      if (selectedEntity === 'schools' && fieldConfig.name === 'region_id') {
        const regionName = currentRecord?.region?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {regionName || '—'}
          </Typography>
        );
      }
      if (selectedEntity === 'schools' && fieldConfig.name === 'consortium_id') {
        const consortiumName = currentRecord?.consortium?.name || currentRecord?.consortium_name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {consortiumName || '—'}
          </Typography>
        );
      }
      if (selectedEntity === 'schools' && fieldConfig.name === 'exam_stages_text') {
        const stages = Array.isArray(currentRecord?.exam_stages) ? currentRecord.exam_stages : [];
        const text = stages.map(stageToReadableLine).join('\n');
        return (
          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-line' }}>
            {text || '—'}
          </Typography>
        );
      }
      if (selectedEntity === 'schools' && fieldConfig.name === 'selection_type') {
        const labels = {
          super_selective: 'Super-selective',
          consortium: 'Consortium',
          all_in_one: 'All-in-one',
          school_specific: 'School-specific',
          other: 'Other',
        };
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {labels[currentRecord?.selection_type] || currentRecord?.selection_type || '—'}
          </Typography>
        );
      }
      if (
        selectedEntity === 'schools' &&
        ['entry_year', 'registration_deadline', 'exam_date'].includes(fieldConfig.name)
      ) {
        const cycle = currentRecord?.latest_admission_cycle || currentRecord?.latestAdmissionCycle;
        let value = cycle?.[fieldConfig.name];
        if (fieldConfig.name !== 'entry_year' && value) {
          value = String(value).slice(0, 10);
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value || '—'}
          </Typography>
        );
      }

      if (
        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
        fieldConfig.name === 'subject_id'
      ) {
        const subjectName = record?.subject?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {subjectName || '—'}
          </Typography>
        );
      }
      if (
        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
        fieldConfig.name === 'school_year_id'
      ) {
        const sy = record?.school_year || record?.schoolYear;
        const syName = sy?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {syName || '—'}
          </Typography>
        );
      }
      // Topics/sub_topics carry status (0/1) directly on the topics row.
      if (
        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
        fieldConfig.name === 'status'
      ) {
        const st = record?.status;
        const n = st === undefined || st === null ? null : Number(st);
        let label = '—';
        let chipColor = 'default';
        if (n === 0) {
          label = 'Inactive';
          chipColor = 'warning';
        } else if (n === 1) {
          label = 'Active';
          chipColor = 'success';
        } else if (n != null && !Number.isNaN(n)) {
          label = String(n);
          chipColor = 'default';
        }
        return (
          <Chip
            label={label}
            size="small"
            color={chipColor}
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
      }
      if (
        (selectedEntity === 'topics' || selectedEntity === 'sub_topics') &&
        fieldConfig.name === 'course_id'
      ) {
        const courseName = record?.course?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {courseName || '—'}
          </Typography>
        );
      }
      // Sub Topics: parent topic name comes from the master topics parent relation.
      if (selectedEntity === 'sub_topics' && fieldConfig.name === 'course_topic_id') {
        const topicName = record?.parent?.name;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {topicName || '—'}
          </Typography>
        );
      }
      // Special handling for Academic Years - parse start_end_year to display start_year and end_year
      if (selectedEntity === 'academic_years' && (fieldConfig.name === 'start_year' || fieldConfig.name === 'end_year')) {
        const startEndYear = currentRecord?.start_end_year;
        
        // Debug logging for Academic Years parsing
        if (process.env.NODE_ENV === 'development') {
          console.log('Academic Years parsing:', {
            fieldName: fieldConfig.name,
            startEndYear: startEndYear,
            currentRecord: currentRecord
          });
        }
        
        if (startEndYear && typeof startEndYear === 'string' && startEndYear.includes('/')) {
          const [startYear, endYear] = startEndYear.split('/');
          const displayValue = fieldConfig.name === 'start_year' ? startYear : endYear;
          return (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {displayValue}
            </Typography>
          );
        }
        return <Typography variant="body2" color="text.secondary">—</Typography>;
      }

      if (selectedEntity === 'reference_books' && fieldConfig.name === 'pdf_file') {
        const pdfUrl = currentRecord?.pdf_url;
        const epubUrl = currentRecord?.epub_url;
        if (!pdfUrl && !epubUrl) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {pdfUrl && <Chip size="small" label="PDF" color="primary" variant="outlined" />}
            {epubUrl && <Chip size="small" label="EPUB" color="secondary" variant="outlined" />}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setBookViewer({ title: currentRecord?.title, pdfUrl, epubUrl, bookId: currentRecord?.id });
              }}
            >
              View
            </Button>
          </Box>
        );
      }

      if (selectedEntity === 'reference_books' && fieldConfig.name === 'epub_file') {
        return null;
      }

      if (Array.isArray(value) && value.length === 0) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
      }
      if (!value && value !== 0 && value !== false) return <Typography variant="body2" color="text.secondary">—</Typography>;

      switch (fieldConfig.type) {
        case 'select':
          // Handle status fields with colored chips
          if (fieldConfig.name === 'status' && fieldConfig.options) {
            const option = fieldConfig.options.find(opt => 
              opt.value === value || opt.value === parseInt(value) || opt.value === String(value)
            );
            
            // Debug logging for status field rendering
            if (process.env.NODE_ENV === 'development') {
              console.log('Status field debug:', {
                fieldName: fieldConfig.name,
                value: value,
                valueType: typeof value,
                options: fieldConfig.options,
                foundOption: option
              });
            }
            // Enhanced color mapping for all status types
            const colors = { 
              // Numeric statuses (1=Pending, 2=Approved, 3=Rejected)
              1: 'warning', 
              2: 'success', 
              3: 'error',
              // String statuses for schools
              'active': 'success', 
              'inactive': 'warning',
              // Additional safety for any other values
              'pending': 'warning',
              'approved': 'success', 
              'rejected': 'error'
            };
            
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip
                  label={option?.label || value}
                  size="small"
                  color={colors[value] || 'default'}
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
                {/* Show deletion info for status fields too */}
                {currentRecord?.deleted_at && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 0.5,
                    mt: 0.5,
                    p: 0.5,
                    backgroundColor: 'error.light',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'error.main'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: 'error.dark', 
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      🗑️ Deleted: {new Date(currentRecord.deleted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          }
          
          // Handle academic_year_id for weeks
          if (fieldConfig.name === 'academic_year_id') {
            let academicYearDisplay = 'N/A';
            if (record.academic_year) {
              if (record.academic_year.start_end_year) {
                academicYearDisplay = record.academic_year.start_end_year;
              } else if (record.academic_year.start_year && record.academic_year.end_year) {
                academicYearDisplay = `${record.academic_year.start_year}/${record.academic_year.end_year}`;
              }
            } else if (value && academicYears.length > 0) {
              const foundYear = academicYears.find(year => year.id === value);
              if (foundYear) {
                if (foundYear.start_end_year) {
                  academicYearDisplay = foundYear.start_end_year;
                } else if (foundYear.start_year && foundYear.end_year) {
                  academicYearDisplay = `${foundYear.start_year}/${foundYear.end_year}`;
                }
              }
            }
            return (
              <Chip
                label={academicYearDisplay}
                size="small"
                color={academicYearDisplay !== 'N/A' ? 'primary' : 'default'}
                variant="outlined"
              />
            );
          }
          
          // Regular select field
          const option = fieldConfig.options?.find(opt => opt.value === value);
          return (
            <Chip
              label={option?.label || value}
              size="small"
              color="info"
              variant="outlined"
            />
          );

        case 'email':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">📧</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {value}
              </Typography>
            </Box>
          );

        case 'url':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">🔗</Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                onClick={() => window.open(value, '_blank')}
              >
                {value}
              </Typography>
            </Box>
          );

        case 'tel':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">📞</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {value}
              </Typography>
            </Box>
          );

        case 'datetime-local':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">📅</Typography>
              <Typography variant="body2">
                {new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} {new Date(value).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})}
              </Typography>
            </Box>
          );

        case 'number':
          return (
            <Chip
              label={value.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              size="small"
              color="secondary"
              variant="outlined"
            />
          );

        case 'checkbox':
          return (
            <Chip
              label={value ? '★ Primary' : 'Secondary'}
              size="small"
              color={value ? 'warning' : 'default'}
              variant={value ? 'filled' : 'outlined'}
            />
          );

        case 'file':
          return value ? (
            <Button
              size="small"
              variant="outlined"
              href={value}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              View PDF
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          );

        case 'textarea': {
          const text = Array.isArray(value)
            ? value.map((v) => String(v).trim()).filter(Boolean).join('\n')
            : String(value ?? '');
          if (!text.trim()) {
            return <Typography variant="body2" color="text.secondary">—</Typography>;
          }
          const preview = text.replace(/\n+/g, ' · ');
          return (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 240,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={text}
            >
              {preview}
            </Typography>
          );
        }

        default: // text
          return (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value}
            </Typography>
          );
      }
    };

    const rowSx = {
      opacity: isDeleted && !isRoleEntity ? 0.7 : 1,
      position: 'relative',
      backgroundColor: isDeleted && !isRoleEntity ? 'rgba(244, 67, 54, 0.02)' : 'transparent',
      '&:hover': {
        backgroundColor: isDeleted && !isRoleEntity
          ? 'rgba(244, 67, 54, 0.06)'
          : 'rgba(102, 126, 234, 0.04)',
      },
      '&::before': isDeleted && !isRoleEntity ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: 'error.main'
      } : {}
    };

    const renderCells = ({ attributes, listeners }) => (
      <>
        {(isTopicsReorderMode || isSubTopicsReorderMode) && (
          <TableCell sx={{ width: 44, pl: 1 }}>
            <IconButton
              size="small"
              disabled={reorderBusy}
              {...attributes}
              {...listeners}
              sx={{
                cursor: reorderBusy ? 'default' : 'grab',
                opacity: reorderBusy ? 0.4 : 0.9,
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}

        {/* ID Column */}
        <TableCell>
          <Chip
            label={`#${record.id}`}
            size="small"
            color="primary"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        </TableCell>

        {/* Dynamic Field Columns */}
        {getTableFields(selectedEntity).map((fieldConfig) => (
          <TableCell key={fieldConfig.name}>
            {renderFieldValue(fieldConfig, record[fieldConfig.name], record)}
          </TableCell>
        ))}

        {/* Created At Column - Hidden for Academic Years */}
        {selectedEntity !== 'academic_years' && (
          <TableCell>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">📅</Typography>
                <Typography variant="body2">
                  {record.created_at ? new Date(record.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </Typography>
              </Box>
              {isDeleted && record.deleted_at && selectedEntity !== 'roles' && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  mt: 0.5,
                  p: 0.5,
                  backgroundColor: 'error.light',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'error.main'
                }}>
                  <Typography variant="body2" sx={{ fontSize: '12px' }}>🗑️</Typography>
                  <Typography variant="caption" sx={{ 
                    color: 'error.dark', 
                    fontWeight: 600,
                    fontSize: '11px'
                  }}>
                    Deleted: {new Date(record.deleted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>
              )}
            </Box>
          </TableCell>
        )}

        {/* Status Column - Only show if entity doesn't have its own status field */}
        {(() => {
          const hasStatusField = config.fields.some(
            (field) => field.name === 'status' || field.name === 'is_active'
          );
          if (process.env.NODE_ENV === 'development') {
            console.log(`Row render - Entity: ${selectedEntity}, Has Status Field: ${hasStatusField}`);
          }
          return !hasStatusField && selectedEntity !== 'schools';
        })() && (
          <TableCell>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Chip 
                label={isDeleted ? 'Inactive' : 'Active'}
                color={isDeleted ? 'error' : 'success'} 
                size="small"
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
              {isDeleted && record.deleted_at && selectedEntity !== 'roles' && (
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontSize: '10px',
                  textAlign: 'center'
                }}>
                  {new Date(record.deleted_at).toLocaleString('en-GB', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              )}
            </Box>
          </TableCell>
        )}

        {/* Actions Column */}
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="View">
              <IconButton 
                onClick={() => handleView(record)} 
                size="small"
                disabled={!canAccessEntityTab(selectedEntity) || deletingRecordId === record.id || restoringRecordId === record.id}
                sx={{
                  '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' }
                }}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
            {!isDeleted && (
              <>
                <Tooltip title="Edit">
                  <IconButton 
                    onClick={() => handleEdit(record)} 
                    size="small" 
                    color="primary"
                    disabled={!canUpdateEntity(selectedEntity) || deletingRecordId === record.id || restoringRecordId === record.id}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                {canDeleteEntity(selectedEntity) && (
                <Tooltip title={deletingRecordId === record.id ? (selectedEntity === 'roles' ? "Setting inactive..." : "Deleting...") : (selectedEntity === 'roles' ? "Set Inactive" : "Delete")}>
                  <IconButton 
                    onClick={() => handleDelete(record)} 
                    size="small" 
                    color="error"
                    disabled={deletingRecordId === record.id || restoringRecordId === record.id}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                    }}
                  >
                    {deletingRecordId === record.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #f44336',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' }
                            }
                          }}
                        />
                      </Box>
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                </Tooltip>
                )}
              </>
            )}
            {isDeleted && (
              <Tooltip title={restoringRecordId === record.id ? (selectedEntity === 'roles' ? "Setting active..." : "Restoring...") : (selectedEntity === 'roles' ? "Set Active" : "Restore")}>
                <IconButton 
                  onClick={() => handleRestore(record)} 
                  size="small" 
                  color="success"
                  disabled={!canUpdateEntity(selectedEntity) || deletingRecordId === record.id || restoringRecordId === record.id}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                  }}
                >
                  {restoringRecordId === record.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          border: '2px solid #f3f3f3',
                          borderTop: '2px solid #4caf50',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <RestoreIcon />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </>
    );

    if (isTopicsReorderMode || isSubTopicsReorderMode) {
      return (
        <SortableMasterRow
          rowId={record.id}
          disabled={reorderBusy}
          sx={rowSx}
          renderCells={renderCells}
        />
      );
    }

    return (
      <TableRow key={record.id} sx={rowSx}>
        {renderCells({ attributes: {}, listeners: {} })}
      </TableRow>
    );
  };

  if (entitiesLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (entities.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No master form sections are available for your account. Ask an administrator to grant view,
          create, update, or delete permissions for the entities you need.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      {/* Master Data (entity management) */}
      <Box>
        <>
      {/* Fancy Header */}
      <Box sx={{ 
        mb: 4,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 4,
        color: 'white',
        boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box sx={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              p: 2,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <Typography sx={{ fontSize: '32px' }}>🗂️</Typography>
            </Box>
            <Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                mb: 0.5
              }}>
                Master Form Management
              </Typography>
              <Typography variant="h6" sx={{ 
                opacity: 0.9,
                fontWeight: 400
              }}>
                Manage all master data entities in the system
              </Typography>
            </Box>
          </Box>
          
          {/* Stats Cards */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 3,
              p: 2,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              minWidth: 150
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <StorageIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0 }}>
                    {entities.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Available Entities
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 3,
              p: 2,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              minWidth: 150
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: '16px' }}>📊</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0 }}>
                    {totalRecords}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Records
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 3,
              p: 2,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              minWidth: 150
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: '16px' }}>✅</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0 }}>
                    {records.filter(r => r.deleted_at === null).length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Records
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {includeDeleted && (
              <Box sx={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 3,
                p: 2,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                minWidth: 150
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '16px' }}>🗑️</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0 }}>
                      {records.filter(r => r.deleted_at !== null).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Deleted Records
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Card sx={{
        borderRadius: 4,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(102, 126, 234, 0.1)',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: 0 }}>
          {/* Entity Selection Tabs */}
          <Box sx={{ 
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            p: 3,
            mb: 0
          }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '14px',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: 'white',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)'
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 2
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                  height: 3,
                  borderRadius: 1.5
                }
              }}
            >
              {entities.map((entity, index) => (
                <Tab 
                  key={entity} 
                  label={ENTITY_CONFIGS[entity]?.name || entity}
                  onClick={() => handleEntityChange(entity)}
                />
              ))}
            </Tabs>
          </Box>

          {selectedEntity && (
            <>
              {selectedEntity === 'schools' && schoolRegionSummary.length > 0 && (
                <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                  {schoolRegionSummary.map((row) => (
                    <Chip
                      key={row.region_id ?? row.region_name}
                      onClick={() =>
                        setSchoolListFilters((prev) => ({
                          ...prev,
                          region_id: row.region_id ? String(row.region_id) : '',
                        }))
                      }
                      label={`${row.region_name}: ${row.grammar} Grammar · ${row.independent} Independent · ${row.total} total`}
                      variant={
                        String(schoolListFilters.region_id) === String(row.region_id || '')
                          ? 'filled'
                          : 'outlined'
                      }
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
              {/* Controls */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                p: 3,
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">
                    {ENTITY_CONFIGS[selectedEntity]?.name || selectedEntity} ({totalRecords})
                  </Typography>
                  {selectedEntity === 'schools' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        placeholder="Search schools, categories, regions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 280, backgroundColor: 'white', borderRadius: 2 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Region</InputLabel>
                        <Select
                          value={schoolListFilters.region_id || ''}
                          onChange={(e) =>
                            setSchoolListFilters((prev) => ({ ...prev, region_id: e.target.value }))
                          }
                          label="Region"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All Regions</MenuItem>
                          {regionsList.map((region) => (
                            <MenuItem key={region.id} value={region.id}>{region.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={schoolListFilters.target_school_id || ''}
                          onChange={(e) =>
                            setSchoolListFilters((prev) => ({ ...prev, target_school_id: e.target.value }))
                          }
                          label="Category"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All Categories</MenuItem>
                          {schoolCategoriesList.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Intake</InputLabel>
                        <Select
                          value={schoolListFilters.gender || ''}
                          onChange={(e) =>
                            setSchoolListFilters((prev) => ({ ...prev, gender: e.target.value }))
                          }
                          label="Intake"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All Intakes</MenuItem>
                          {Object.entries(SCHOOL_GENDER_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Selection type</InputLabel>
                        <Select
                          value={schoolListFilters.selection_type || ''}
                          onChange={(e) => {
                            const next = e.target.value;
                            setSchoolListFilters((prev) => {
                              const stillRelevant = !prev.consortium_name
                                || !next
                                || schoolConsortiumOptions.some(
                                  (item) => item.name === prev.consortium_name && item.selection_type === next
                                );
                              return {
                                ...prev,
                                selection_type: next,
                                consortium_name: stillRelevant ? prev.consortium_name : '',
                              };
                            });
                          }}
                          label="Selection type"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All selection types</MenuItem>
                          {Object.entries(SCHOOL_SELECTION_TYPE_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 280 }}>
                        <InputLabel>Consortium</InputLabel>
                        <Select
                          value={schoolListFilters.consortium_name || ''}
                          onChange={(e) =>
                            setSchoolListFilters((prev) => ({ ...prev, consortium_name: e.target.value }))
                          }
                          label="Consortium"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All consortiums</MenuItem>
                          {(schoolListFilters.selection_type
                            ? schoolConsortiumOptions.filter((item) => !item.selection_type || item.selection_type === schoolListFilters.selection_type)
                            : schoolConsortiumOptions
                          ).map((item) => (
                            <MenuItem key={`${item.selection_type}-${item.name}`} value={item.name}>
                              {item.name} ({item.school_count})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {(selectedEntity === 'topics' || selectedEntity === 'sub_topics') && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        placeholder="Search topics..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 250, backgroundColor: 'white', borderRadius: 2 }}
                      />
                      
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Subject</InputLabel>
                        <Select
                          value={topicListFilters.subject_id || ''}
                          onChange={(e) =>
                            setTopicListFilters((prev) => ({ ...prev, subject_id: e.target.value }))
                          }
                          label="Subject"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All Subjects</MenuItem>
                          {topicsSubjectsList.map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>School Year</InputLabel>
                        <Select
                          value={topicListFilters.school_year_id || ''}
                          onChange={(e) =>
                            setTopicListFilters((prev) => ({ ...prev, school_year_id: e.target.value }))
                          }
                          label="School Year"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All Years</MenuItem>
                          {schoolYearsForTopicsList.map(sy => (
                            <MenuItem key={sy.id} value={sy.id}>{sy.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {selectedEntity === 'sub_topics' && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Topic</InputLabel>
                          <Select
                            value={topicListFilters.parent_topic_id || ''}
                            onChange={(e) =>
                              setTopicListFilters((prev) => ({ ...prev, parent_topic_id: e.target.value }))
                            }
                            label="Topic"
                            sx={{ backgroundColor: 'white', borderRadius: 2 }}
                            disabled={!topicListFilters.subject_id || subTopicsTopicsLoading}
                          >
                            <MenuItem value="">All Topics</MenuItem>
                            {dedupedSubTopicsTopicsList.map((t) => (
                              <MenuItem key={t.master_topic_id} value={t.master_topic_id}>
                                {t.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={topicListFilters.status === undefined ? '' : topicListFilters.status}
                          onChange={(e) =>
                            setTopicListFilters((prev) => ({ ...prev, status: e.target.value }))
                          }
                          label="Status"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value={1}>Active</MenuItem>
                          <MenuItem value={0}>Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {selectedEntity === 'reference_books' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        placeholder="Search by title, author or genre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 280, backgroundColor: 'white', borderRadius: 2 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Subject</InputLabel>
                        <Select
                          value={refBookFilters.subject}
                          onChange={(e) => setRefBookFilters((prev) => ({ ...prev, subject: e.target.value }))}
                          label="Subject"
                          sx={{ backgroundColor: 'white', borderRadius: 2 }}
                        >
                          <MenuItem value="">All Subjects</MenuItem>
                          <MenuItem value="english">English</MenuItem>
                          <MenuItem value="math">Math</MenuItem>
                          <MenuItem value="verbal">Verbal Reasoning</MenuItem>
                          <MenuItem value="non_verbal">Non-Verbal Reasoning</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeDeleted}
                        onChange={(e) => setIncludeDeleted(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include Deleted"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRecords}
                    disabled={loading}
                    sx={{
                      borderRadius: 3,
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      color: '#667eea',
                      backdropFilter: 'blur(10px)',
                      background: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        borderColor: '#667eea',
                        background: 'rgba(102, 126, 234, 0.1)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  {selectedEntity === 'schools' && (
                    <Button
                      variant="outlined"
                      startIcon={
                        exportingSchoolsCsv
                          ? <CircularProgress size={16} color="inherit" />
                          : <FileDownloadIcon />
                      }
                      onClick={exportSchoolsCsv}
                      disabled={exportingSchoolsCsv || loading}
                      sx={{
                        borderRadius: 3,
                        borderColor: 'rgba(16, 185, 129, 0.35)',
                        color: '#059669',
                        backdropFilter: 'blur(10px)',
                        background: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          borderColor: '#059669',
                          background: 'rgba(16, 185, 129, 0.1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {exportingSchoolsCsv ? 'Exporting...' : 'Export CSV'}
                    </Button>
                  )}
                  {selectedEntity === 'schools' && canUpdateEntity(selectedEntity) && (
                    <Tooltip title="Research every school matching the current filters on the web, then review what changed before anything is saved.">
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={
                            enrichBulkBusy
                              ? <CircularProgress size={16} color="inherit" />
                              : <AutoAwesomeIcon />
                          }
                          onClick={startBulkEnrichment}
                          disabled={enrichBulkBusy || loading}
                          sx={{
                            borderRadius: 3,
                            borderColor: 'rgba(124, 58, 237, 0.35)',
                            color: '#6d28d9',
                            backdropFilter: 'blur(10px)',
                            background: 'rgba(255,255,255,0.7)',
                            '&:hover': {
                              borderColor: '#6d28d9',
                              background: 'rgba(124, 58, 237, 0.1)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {enrichBulkBusy ? 'Starting...' : 'Update from web (AI)'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {selectedEntity === 'schools' && canDeleteEntity(selectedEntity) && (
                    <Tooltip title="Review the rows the paper importer created from cover-page labels: remove the ones that are not schools, merge duplicates, activate the real ones.">
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<CleaningServicesIcon />}
                          onClick={() => setTriageOpen(true)}
                          disabled={loading}
                          sx={{
                            borderRadius: 3,
                            borderColor: 'rgba(217, 119, 6, 0.35)',
                            color: '#b45309',
                            backdropFilter: 'blur(10px)',
                            background: 'rgba(255,255,255,0.7)',
                            '&:hover': {
                              borderColor: '#b45309',
                              background: 'rgba(217, 119, 6, 0.1)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(217, 119, 6, 0.25)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Clean up
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {selectedEntity === 'weeks' && (
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchAcademicYears}
                      disabled={academicYearsLoading}
                      sx={{
                        borderRadius: 3,
                        borderColor: 'rgba(156, 39, 176, 0.3)',
                        color: '#9c27b0',
                        backdropFilter: 'blur(10px)',
                        background: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          borderColor: '#9c27b0',
                          background: 'rgba(156, 39, 176, 0.1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {academicYearsLoading ? 'Loading...' : 'Reload Years'}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    disabled={!canCreateEntity(selectedEntity)}
                    sx={{
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.5)'
                      },
                      transition: 'all 0.3s ease',
                      fontWeight: 600
                    }}
                  >
                    Add New
                  </Button>
                </Box>
              </Box>

              {/* Helper Alert for Weeks */}
              {selectedEntity === 'weeks' && academicYears.length === 0 && !academicYearsLoading && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Academic Years Required:</strong> To create weeks, you need to have academic years first. 
                    Please create academic years in the "Academic Years" tab, then return to this section.
                  </Typography>
                </Alert>
              )}

              {/* Debug Info for Weeks */}
              {selectedEntity === 'weeks' && process.env.NODE_ENV === 'development' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Debug Info:</strong> Academic Years Loaded: {academicYears.length}, 
                    Records: {records.length}, Loading: {loading ? 'Yes' : 'No'}
                  </Typography>
                </Alert>
              )}

              {/* Records Table */}
              <TableContainer 
                component={Paper} 
                sx={{ 
                  m: 3,
                  borderRadius: 3,
                  overflow: 'auto', // Enable both horizontal and vertical scrolling
                  maxWidth: '100%', // Ensure container doesn't exceed parent width
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
                  // Custom scrollbar styling to match theme
                  '&::-webkit-scrollbar': {
                    height: '8px', // Horizontal scrollbar height
                    width: '8px'   // Vertical scrollbar width
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(0,0,0,0.05)',
                    borderRadius: '10px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.2)'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'linear-gradient(90deg, #5a6fd8 0%, #6a4190 100%)'
                  },
                  // Firefox support
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#667eea rgba(0,0,0,0.05)'
                }}
              >
                <Table sx={{
                  minWidth: 1200, // Minimum width to ensure proper column spacing
                  '& .MuiTableHead-root': {
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    color: 'white',
                    fontWeight: 700,
                    borderBottom: 'none',
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap', // Prevent header text wrapping
                    minWidth: 120 // Minimum width for each column
                  },
                  '& .MuiTableBody-root .MuiTableRow-root': {
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(102, 126, 234, 0.02)'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.06)',
                      transform: 'scale(1.001)',
                      transition: 'all 0.2s ease'
                    }
                  },
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap', // Prevent cell content wrapping
                    overflow: 'hidden',
                    textOverflow: 'ellipsis' // Add ellipsis for long content
                  }
                }}>
                  <TableHead>
                    <TableRow>
                      {renderTableHeaders()}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, index) => {
                        const config = ENTITY_CONFIGS[selectedEntity];
                        // Calculate columns: ID + Fields + Actions + (Created At if not academic_years) + (Status if no status field and not schools)
                        const tableFieldCount = getTableFields(selectedEntity).length;
                        const hasStatusField =
                          config?.fields.some(
                            (field) => field.name === 'status' || field.name === 'is_active'
                          ) || false;
                        let columnCount = tableFieldCount + 2; // +2 for ID, Actions
                        if (isTopicsReorderMode || isSubTopicsReorderMode) columnCount += 1; // drag handle
                        if (selectedEntity !== 'academic_years') columnCount += 1; // +1 for Created At
                        if (!hasStatusField && selectedEntity !== 'schools') columnCount += 1; // +1 for Status column
                        
                        return (
                          <TableRow key={index}>
                            {Array.from({ length: columnCount }).map((_, cellIndex) => (
                              <TableCell key={cellIndex}>
                                <Skeleton variant="text" />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })
                    ) : records.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={(() => {
                            const config = ENTITY_CONFIGS[selectedEntity];
                            const tableFieldCount = getTableFields(selectedEntity).length;
                            const hasStatusField =
                              config?.fields.some(
                                (field) => field.name === 'status' || field.name === 'is_active'
                              ) || false;
                            let columnCount = tableFieldCount + 2; // +2 for ID, Actions
                            if (isTopicsReorderMode || isSubTopicsReorderMode) columnCount += 1; // drag handle
                            if (selectedEntity !== 'academic_years') columnCount += 1; // +1 for Created At
                            if (!hasStatusField && selectedEntity !== 'schools') columnCount += 1; // +1 for Status column
                            return columnCount;
                          })()} 
                          align="center"
                        >
                          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" color="text.secondary">
                              📋
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              No {ENTITY_CONFIGS[selectedEntity]?.name.toLowerCase() || 'records'} found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Click "Add New" to create your first {ENTITY_CONFIGS[selectedEntity]?.name.slice(0, -1).toLowerCase() || 'record'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (isTopicsReorderMode || isSubTopicsReorderMode) ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleReorderDragEnd}
                      >
                        <SortableContext
                          items={records.map((r) => String(r.id))}
                          strategy={verticalListSortingStrategy}
                        >
                          {records.map(renderTableRow)}
                        </SortableContext>
                      </DndContext>
                    ) : (
                      records.map(renderTableRow)
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {(totalPages > 1 || selectedEntity === 'schools') && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    mt: 3,
                    flexWrap: 'wrap',
                  }}
                >
                  {selectedEntity === 'schools' && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Per page</InputLabel>
                      <Select
                        label="Per page"
                        value={perPage}
                        onChange={(e) => {
                          setPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Page {currentPage} of {Math.max(totalPages, 1)} · {totalRecords} total
                  </Typography>
                  <Pagination
                    count={Math.max(totalPages, 1)}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

        </>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth={selectedEntity === 'schools' ? 'lg' : 'md'}
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <span>
            {viewMode ? 'View' : editMode ? 'Edit' : 'Create'} {ENTITY_CONFIGS[selectedEntity]?.name?.slice(0, -1) || 'Record'}
          </span>
          {selectedEntity === 'schools' && editMode && currentRecord?.id && canUpdateEntity(selectedEntity) && (
            <Tooltip title="Look this school up on the web and review what has changed since we last stored it.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    enrichSingleBusy
                      ? <CircularProgress size={14} color="inherit" />
                      : <AutoAwesomeIcon fontSize="small" />
                  }
                  onClick={() => enrichSingleSchool(currentRecord.id, watch('entry_year'))}
                  disabled={enrichSingleBusy}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {enrichSingleBusy ? 'Researching...' : 'Fetch latest (AI)'}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {/* Instruction notes for different entities */}
            {selectedEntity === 'weeks' && (
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(102, 126, 234, 0.2)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: '18px' }}>💡</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Smart Date Suggestion
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                  <strong>How it works:</strong> Select an Academic Year and enter a Week Number (1-52). 
                  The system will automatically calculate the correct start and end dates following the Monday-Sunday pattern 
                  with proper timing (Monday 00:00 → Sunday 22:00).
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.5 }}>
                  <strong>Tip:</strong> You can still manually adjust the suggested dates if needed.
                </Typography>
              </Box>
            )}
            
            {selectedEntity === 'academic_years' && (
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: '18px' }}>🎓</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    Academic Year Guidelines
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                  <strong>Auto-fill feature:</strong> Enter the Start Year and the End Year will automatically be set to Start Year + 1. 
                  Academic years must be consecutive (e.g., 2025/2026, not 2025/2028).
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.5 }}>
                  <strong>Valid range:</strong> Years must be between 1900-2100.
                </Typography>
              </Box>
            )}

            {/* General instruction for other entities with special features */}
            {(selectedEntity !== 'weeks' && selectedEntity !== 'academic_years') && (
              ENTITY_CONFIGS[selectedEntity]?.fields?.some(field => field.name === 'status') && (
                <Box sx={{ 
                  mb: 2, 
                  p: 1.5, 
                  backgroundColor: 'rgba(158, 158, 158, 0.06)',
                  borderRadius: 1.5,
                  border: '1px solid rgba(158, 158, 158, 0.15)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px' }}>ℹ️</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      <strong>Status:</strong> Select Pending (review needed), Approved (active), or Rejected (inactive)
                    </Typography>
                  </Box>
                </Box>
              )
            )}

            <Box sx={{ mt: 1 }}>
              {renderFormFields()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Cancel
            </Button>
            {!viewMode && (
              <Button 
                type="submit" 
                variant="contained"
                disabled={submitting}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                  }
                }}
              >
                {submitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    />
                    {editMode ? 'Updating...' : 'Creating...'}
                  </Box>
                ) : (
                  editMode ? 'Update' : 'Create'
                )}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          pb: 1,
          color: confirmDialog.severity === 'error' ? 'error.main' : 
                confirmDialog.severity === 'info' ? 'info.main' : 'warning.main',
          fontSize: '1.25rem',
          fontWeight: 600
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: confirmDialog.severity === 'error' ? 'error.light' : 
                           confirmDialog.severity === 'info' ? 'info.light' : 'warning.light',
            color: confirmDialog.severity === 'error' ? 'error.main' : 
                   confirmDialog.severity === 'info' ? 'info.main' : 'warning.main',
            fontSize: '20px'
          }}>
            {confirmDialog.severity === 'error' && '🗑️'}
            {confirmDialog.severity === 'info' && '♻️'}
            {confirmDialog.severity === 'warning' && '⚠️'}
          </Box>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ 
            whiteSpace: 'pre-line', 
            lineHeight: 1.6,
            color: 'text.secondary'
          }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={closeConfirmDialog}
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {confirmDialog.cancelText}
          </Button>
          <Button 
            onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
              closeConfirmDialog();
            }}
            variant="contained"
            color={confirmDialog.severity === 'error' ? 'error' : 
                   confirmDialog.severity === 'info' ? 'info' : 'warning'}
            size="large"
            autoFocus
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {confirmDialog.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      <BookViewerDialog
        open={Boolean(bookViewer)}
        onClose={() => setBookViewer(null)}
        title={bookViewer?.title}
        pdfUrl={bookViewer?.pdfUrl}
        epubUrl={bookViewer?.epubUrl}
        bookId={bookViewer?.bookId}
      />

      <SchoolEnrichmentReviewDialog
        open={enrichReviewOpen}
        runId={enrichRunId}
        onClose={() => {
          setEnrichReviewOpen(false);
          setEnrichRunId(null);
        }}
        onApplied={() => {
          fetchRecords();
          fetchSchoolRegionSummary();
        }}
      />

      <SchoolTriageDialog
        open={triageOpen}
        onClose={() => setTriageOpen(false)}
        onApplied={() => {
          fetchRecords();
          fetchSchoolRegionSummary();
        }}
      />
    </Box>
  );
};

export default MasterForm;
